import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { SearchableSelect, SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import CountrySelect from '../../../shared/layout/forms/CountrySelect';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../context/CurrencyProvider';
import { fetchCountryCodes, fetchCurrencies } from '../../../services/Http/Api';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import {
    fetchAdminScannedBill,
    reviewAdminScannedBill,
    rejectAdminScannedBill,
    saveAdminScannedBillPreparedReviewState,
    matchAdminScannedBillItem,
    reprocessAdminScannedBill,
    suggestAdminScannedBillWithAi,
    prepareAdminScannedBillReviewWithGemini,
    bootstrapAdminScannedBillEntities,
    finalizeAdminScannedBill,
    enrichAdminScannedBill,
    fetchAdminScannedBillVenueTypes,
    fetchAdminScannedBillWarrantyTemplateTypes,
    searchAdminScannedBillOrganizations,
    searchAdminScannedBillVenues,
    searchAdminScannedBillManufacturerItems,
    searchAdminScannedBillManufacturerVariants,
} from '../../../services/Http/AdminApi';

// ============================================================================
// DESIGN TOKENS
// ============================================================================
const colors = {
    primary: '#7C3AED',
    primaryHover: '#6D28D9',
    primaryLight: '#EDE9FE',

    real: { bg: '#DCFCE7', border: '#86EFAC', text: '#166534', badge: '#22C55E' },
    virtual: { bg: '#FEF3C7', border: '#FCD34D', text: '#92400E', badge: '#F59E0B' },

    success: { bg: '#ECFDF5', border: '#A7F3D0', text: '#065F46' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' },
    error: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' },
    info: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' },

    surface: '#FFFFFF',
    surfaceHover: '#F9FAFB',
    border: '#E5E7EB',
    borderLight: '#F3F4F6',
    text: '#111827',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
};

const radius = { sm: '6px', md: '10px', lg: '14px', full: '9999px' };

/*
 * Ista skala kao theme.fontSizes, drzana lokalno jer ovaj ekran vec drzi
 * colors i radius lokalno - mijesanje dva izvora u istoj datoteci bilo bi gore
 * od jednog ponavljanja. U rem, da radi rucica na html.
 */
const type = {
    label:   '0.6875rem',  // 11
    meta:    '0.75rem',    // 12
    dense:   '0.8125rem',  // 13
    base:    '0.875rem',   // 14
    lead:    '1rem',       // 16
    title:   '1.25rem',    // 20
};
const MIN_DB_MATCH_CONFIDENCE = 0.80;

/*
 * Nazivi za prikaz. Baza cuva jedan naziv po pojmu; ovdje se prevodi u nesto
 * sto administrator cita. Nepoznata vrijednost se ispisuje kakva jest, jer je
 * bolje vidjeti neocekivan naziv nego prazninu koja skriva da je nesto novo.
 */
const WARRANTY_KIND_LABELS = {
    printed: 'printano na dokumentu',
    manufacturer_published: 'proizvodjacka, objavljena',
    statutory: 'zakonska odgovornost',
    not_applicable: 'ne primjenjuje se',
    unknown: 'trajanje nije utvrdjeno',
};
const warrantyKindLabel = (kind) => WARRANTY_KIND_LABELS[kind] || kind || '—';

const TARGET_TYPE_LABELS = {
    retailer: 'trgovac',
    venue: 'poslovnica',
    manufacturer: 'proizvodjac',
    manufacturer_item: 'proizvod',
    variant: 'varijanta',
    organization_item_sku: 'SKU trgovca',
};
const targetTypeLabel = (t2) => TARGET_TYPE_LABELS[t2] || t2 || '—';

const DECISION_LABELS = {
    linked: 'povezano',
    create_new: 'novi zapis',
    unresolved: 'nije rijeseno',
};
const decisionLabel = (d) => DECISION_LABELS[d] || d || '—';

const STAGE_LABELS = {
    classify: 'klasifikacija',
    extract: 'citanje',
    verify: 'provjera',
    dedup: 'duplikati',
    identify: 'identitet',
    match: 'uparivanje',
    decide: 'odluka',
};
const stageLabel = (st) => STAGE_LABELS[st] || st;

const DOC_TYPE_LABELS = {
    fiscal_receipt: 'fiskalni racun',
    invoice: 'racun / faktura',
    delivery_note: 'otpremnica',
    card_slip: 'slip kartice',
    warranty_card: 'jamstveni list',
    screen_photo: 'fotografija ekrana',
    other: 'ostalo',
};
const docTypeLabel = (d) => DOC_TYPE_LABELS[d] || d || '—';

const CAPTURE_ISSUE_LABELS = {
    angled: 'slikano pod kutom',
    creased: 'zguzvano',
    folded: 'presavijeno',
    glare: 'odsjaj',
    low_light: 'slabo svjetlo',
    blurred: 'mutno',
    faded_print: 'blijed ispis',
    cut_off_edge: 'izrezan rub',
    overlapping_documents: 'vise dokumenata u kadru',
};
const captureIssueLabel = (i) => CAPTURE_ISSUE_LABELS[i] || i;

const formatTokens = (n) => (typeof n === 'number' ? n.toLocaleString('hr-HR') : '—');

/*
 * JSON kolona ne stigne uvijek kao niz.
 *
 * Ako backend nema cast na to polje - stariji deploy, drugi endpoint, ili
 * kolona dodana prije nego je model dopunjen - Laravel vrati sirovi string
 * '["angled"]'. Taj je istinit i neprazan, pa prodje kroz provjeru duljine i
 * srusi se tek na .map. Ovdje se svodi na niz, sto god stigne.
 */
const asList = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        try {
            const parsed = JSON.parse(v);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
};

/*
 * The bill date is a wall clock, not an instant.
 *
 * bill_date and admin_bill_date are `timestamp without time zone` - the date
 * and time printed on the receipt, in whatever country it was printed. Laravel
 * still serialises them with a trailing Z, so `new Date(value)` read them as
 * UTC while getFullYear/getHours below returned the browser's local time. The
 * form therefore showed the receipt time shifted by the local offset, and the
 * save wrote that shifted value straight back into admin_bill_date.
 *
 * Every open-and-save added the offset again. Measured on production: nine
 * bills drifted, in exact multiples of the offset - +2h, +4h, +6h in summer,
 * +1h per save in March - and one crossed midnight, moving a purchase from
 * 2026-06-01 to 2026-06-02. That date sets the warranty start and end.
 *
 * So no conversion at all: take the wall clock as printed.
 */
const toDateTimeLocalValue = (value) => {
    if (!value) return '';
    const wallClock = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (wallClock) {
        const [, year, month, day, hours, minutes] = wallClock;
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (part) => String(part).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizeSelectionMode = (mode, linked, create) => {
    const normalized = typeof mode === 'string' ? mode.trim().toLowerCase() : null;
    if (['link', 'match_existing', 'existing', 'linked'].includes(normalized)) {
        return 'link';
    }
    if (['create', 'create_new', 'new'].includes(normalized)) {
        return 'create';
    }
    if (linked && typeof linked === 'object') {
        return 'link';
    }
    if (create && typeof create === 'object' && Object.values(create).some((value) => value !== null && value !== '')) {
        return 'create';
    }
    return null;
};

const normalizeCurrencyCodeForSave = (value, currencyOptions) => {
    const normalized = String(value || '').trim().toUpperCase();
    if (!normalized) return null;

    const isValid = Array.isArray(currencyOptions) && currencyOptions.some((currency) => {
        const code = String(currency?.code || currency?.key || '').trim().toUpperCase();
        return code === normalized;
    });

    return isValid ? normalized : null;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const AdminScannedBillDetail = () => {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { formatResolvedAmount } = useCurrency();

    // Core state
    const [scan, setScan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // AI state
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState('');
    const [aiResult, setAiResult] = useState(null);
    const [forceAiRun, setForceAiRun] = useState(false);
    const [useProcessedImageForAi, setUseProcessedImageForAi] = useState(true);
    const [useHighDetailImage, setUseHighDetailImage] = useState(false);
    const [geminiPrepareLoading, setGeminiPrepareLoading] = useState(false);
    const [geminiPrepareError, setGeminiPrepareError] = useState('');

    // Currency override (Phase 5A)
    const [adminCurrency, setAdminCurrency] = useState('');
    const [adminCurrencySource, setAdminCurrencySource] = useState('unknown');
    const [suggestedCurrency, setSuggestedCurrency] = useState('');
    const [suggestedCurrencySource, setSuggestedCurrencySource] = useState('unknown');
    const [currencyOptions, setCurrencyOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [billDraft, setBillDraft] = useState({
        billNumber: '',
        billDate: '',
        paymentMethod: '',
        subtotal: '',
        taxAmount: '',
        totalAmount: '',
    });

    // Web enrichment (Phase 5C). Accept is a pure local state update —
    // resolved values flow into retailer/venue/item create-new form fields
    // so that handleProcess naturally sees admin-approved values.
    const [enrichLoading, setEnrichLoading] = useState(false);
    const [enrichError, setEnrichError] = useState('');
    const [enrichResult, setEnrichResult] = useState(null);
    const [enrichForce, setEnrichForce] = useState(false);
    const [acceptedKeys, setAcceptedKeys] = useState(() => new Set());
    const [entityEnrichment, setEntityEnrichment] = useState({
        retailer: true,
        venue: true,
    });

    // Backend OCR state
    const [ocrLanguage, setOcrLanguage] = useState('auto');
    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrError, setOcrError] = useState('');
    const [ocrResult, setOcrResult] = useState(null);

    // Entity selections - tracks what admin has chosen for each entity
    // mode: 'link' | 'create' | null
    // linked: { id, name, is_virtual } - if linking to existing
    // create: { name, address, etc } - if creating new virtual
    const [retailerSelection, setRetailerSelection] = useState({
        mode: null,
        linked: null,
        create: {
            name: '',
            brand_name: '',
            address: '',
            city: '',
            country: '',
            phone: '',
            email: '',
            website: '',
            tax_id: '',
            vat_id: '',
            business_type: '',
        },
    });
    const [venueSelection, setVenueSelection] = useState({
        mode: null,
        linked: null,
        create: {
            name: '',
            address: '',
            city: '',
            country: '',
            phone: '',
            website: '',
            venue_type_id: '',
        },
    });

    // Items - each item can have manufacturer + product selection
    const [itemSelections, setItemSelections] = useState({});
    const [itemEdits, setItemEdits] = useState({});
    // { [itemId]: { manufacturer: { mode, linked, create }, product: { mode, linked, create }, variant: { mode, linked, create } } }

    // Search results
    const [retailerOptions, setRetailerOptions] = useState([]);
    const [venueOptions, setVenueOptions] = useState([]);
    const [venueTypeOptions, setVenueTypeOptions] = useState([]);
    const [warrantyTemplateTypeOptions, setWarrantyTemplateTypeOptions] = useState([]);
    const [manufacturerOptions, setManufacturerOptions] = useState({});
    const [productOptions, setProductOptions] = useState({});
    const [variantOptions, setVariantOptions] = useState({});

    // Processing state
    const [processing, setProcessing] = useState(false);
    const [processError, setProcessError] = useState('');
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectReasonCode, setRejectReasonCode] = useState('unclear_image');
    const [rejectLanguage, setRejectLanguage] = useState('hr');
    const [rejectMessage, setRejectMessage] = useState('');
    const [rejecting, setRejecting] = useState(false);

    const rejectionTemplates = {
        hr: {
            unclear_image: 'Račun nije jasno uslikan. Molimo pokušajte ponovo, tako da svi podaci budu čitljivi.',
            not_a_receipt: 'Dostavljeni dokument nije račun. Molimo skenirajte račun za kupovinu.',
            missing_details: 'Na računu nedostaju podaci potrebni za potvrdu kupovine. Molimo pokušajte ponovo s cijelim računom.',
        },
        en: {
            unclear_image: 'The receipt image is not clear enough. Please scan it again with all details readable.',
            not_a_receipt: 'The submitted document is not a receipt. Please scan your purchase receipt.',
            missing_details: 'The receipt is missing information needed to verify the purchase. Please scan the complete receipt again.',
        },
    };

    const handleRejectBill = async () => {
        const message = rejectMessage.trim();
        if (!message || rejecting) return;
        setRejecting(true);
        try {
            await rejectAdminScannedBill(id, { message, reason_code: rejectReasonCode });
            await loadScan();
            setShowRejectForm(false);
        } catch (err) {
            setProcessError(err?.response?.data?.message || 'Failed to reject bill.');
        } finally {
            setRejecting(false);
        }
    };

    // Notes
    const [notes, setNotes] = useState('');
    const [externalBillId, setExternalBillId] = useState('');
    const [duplicateConflict, setDuplicateConflict] = useState(null);
    const preparedStateHydratedRef = useRef(false);
    const preparedStateSaveTimerRef = useRef(null);
    const preparedStateLastSavedRef = useRef('');

    const filterRetailerCandidates = useCallback((candidates) => {
        if (!Array.isArray(candidates)) return [];
        return candidates.filter((c) => Number(c?.confidence || 0) >= MIN_DB_MATCH_CONFIDENCE);
    }, []);

    // ========================================================================
    // DATA LOADING
    // ========================================================================
    const buildInitialItemSelections = useCallback((data) => {
        const aiMatchedItems = data?.ai_last_response?.matched?.items || [];
        const byLineNumber = new Map(aiMatchedItems.map(ai => [String(ai.line_number), ai]));

        const realItems = Array.isArray(data?.items) ? data.items : [];
        const aiExtractedItems = data?.ai_last_response?.extracted?.items || [];
        const sourceItems = realItems.length > 0
            ? realItems
            : aiExtractedItems.map((aiItem, idx) => ({
                id: `ai-line-${idx + 1}`,
                line_number: idx + 1,
                raw_text: aiItem?.line_text || null,
                extracted_name: aiItem?.name || null,
                extracted_model_number: aiItem?.model_number || aiItem?.variant_model_number || null,
                extracted_ean: aiItem?.ean || null,
                extracted_sku: aiItem?.sku || aiItem?.organization_sku || null,
                extracted_quantity: aiItem?.quantity ?? 1,
                extracted_unit_price: aiItem?.unit_price ?? null,
                extracted_total_price: aiItem?.total_price ?? null,
                matched_manufacturer_id: null,
                matched_manufacturer_item_id: null,
                matched_variant_id: null,
            }));

        const selections = {};
        sourceItems.forEach(item => {
            const aiItem = byLineNumber.get(String(item.line_number));

            const manufacturerLinkedId = item.matched_manufacturer_id || aiItem?.matched_manufacturer_id || null;
            const productLinkedId = item.matched_manufacturer_item_id || aiItem?.matched_manufacturer_item_id || null;
            const variantLinkedId = item.matched_variant_id || aiItem?.matched_variant_id || null;

            selections[item.id] = {
                manufacturer: {
                    mode: manufacturerLinkedId ? 'link' : null,
                    linked: manufacturerLinkedId
                        ? {
                            id: manufacturerLinkedId,
                            name: aiItem?.matched_manufacturer_name || t('Common.manufacturer'),
                            is_virtual: false,
                            confidence: aiItem?.match_confidence_score || null,
                        }
                        : null,
                    create: { name: aiItem?.extracted_brand || aiItem?.extracted_manufacturer_name || data?.ai_last_response?.extracted?.manufacturer_name || '' },
                },
                product: {
                    mode: productLinkedId ? 'link' : null,
                    linked: productLinkedId
                        ? {
                            id: productLinkedId,
                            name: aiItem?.matched_manufacturer_item_name || item.extracted_name || t('Common.product'),
                            is_virtual: false,
                            confidence: aiItem?.match_confidence_score || null,
                        }
                        : null,
                    create: {
                        name: item.extracted_name || aiItem?.extracted_name || '',
                        model_number: item.extracted_model_number || aiItem?.extracted_model_number || '',
                        ean: item.extracted_ean || aiItem?.extracted_ean || '',
                    },
                },
                variant: {
                    mode: variantLinkedId ? 'link' : null,
                    linked: variantLinkedId
                        ? {
                            id: variantLinkedId,
                            sku: aiItem?.matched_variant_sku || item.extracted_sku || '',
                            is_virtual: false,
                        }
                        : null,
                    create: { sku: item.extracted_sku || aiItem?.extracted_sku || '' },
                },
            };
        });

        return selections;
    }, [t]);

    const buildInitialItemEdits = useCallback((data) => {
        const aiExtractedItems = data?.ai_last_response?.extracted?.items || [];
        const realItems = Array.isArray(data?.items) ? data.items : [];
        const realByLine = new Map(realItems.map((item) => [String(item.line_number), item]));
        const sourceItems = aiExtractedItems.length > 0
            ? aiExtractedItems.map((aiItem, idx) => {
                const lineNumber = aiItem?.line_number || (idx + 1);
                const persisted = realByLine.get(String(lineNumber));
                const persistedWarrantyResolution = persisted?.admin_prepared_review_payload?.warranty_resolution || {};
                return {
                    id: persisted?.id || `ai-line-${lineNumber}`,
                    line_number: lineNumber,
                    raw_text: persisted?.admin_corrected_raw_text ?? persisted?.raw_text ?? aiItem?.line_text ?? '',
                    extracted_name: persisted?.admin_corrected_name ?? persisted?.extracted_name ?? aiItem?.name ?? '',
                    extracted_model_number: persisted?.admin_corrected_model_number ?? persisted?.extracted_model_number ?? aiItem?.model_number ?? aiItem?.variant_model_number ?? '',
                    extracted_ean: persisted?.admin_corrected_ean ?? persisted?.extracted_ean ?? aiItem?.ean ?? '',
                    extracted_sku: persisted?.admin_corrected_sku ?? persisted?.extracted_sku ?? aiItem?.sku ?? aiItem?.organization_sku ?? '',
                    extracted_quantity: persisted?.admin_corrected_quantity ?? persisted?.extracted_quantity ?? aiItem?.quantity ?? '',
                    extracted_unit_price: persisted?.admin_corrected_unit_price ?? persisted?.extracted_unit_price ?? aiItem?.unit_price ?? '',
                    extracted_total_price: persisted?.admin_corrected_total_price ?? persisted?.extracted_total_price ?? aiItem?.total_price ?? '',
                    extracted_discount: persisted?.admin_corrected_discount ?? persisted?.extracted_discount ?? aiItem?.discount ?? '',
                    extracted_tax: persisted?.admin_corrected_tax ?? persisted?.extracted_tax ?? aiItem?.tax ?? '',
                    admin_warranty_duration: persisted?.admin_warranty_duration ?? persistedWarrantyResolution?.duration_months ?? '',
                    admin_warranty_template_type_id: persisted?.admin_warranty_template_type_id ?? persistedWarrantyResolution?.template_type_id ?? '',
                    // Carried through so the exclusion checkbox reflects the saved
                    // state; without these the reducer below always read undefined.
                    excluded_from_processing: persisted?.excluded_from_processing ?? false,
                    admin_exclusion_note: persisted?.admin_exclusion_note ?? '',
                };
            })
            : realItems;

        return sourceItems.reduce((acc, item) => {
            const warrantyResolution = item?.admin_prepared_review_payload?.warranty_resolution || {};
            acc[item.id] = {
                // Prefer the admin correction over the raw extraction, matching how
                // the AI branch above resolves it. Without this a corrected bill
                // reopened as the original OCR text ("EEAPECKS" for "FLEXPICKS").
                raw_text: item.admin_corrected_raw_text ?? item.raw_text ?? '',
                name: item.admin_corrected_name ?? item.extracted_name ?? '',
                model_number: item.admin_corrected_model_number ?? item.extracted_model_number ?? '',
                ean: item.admin_corrected_ean ?? item.extracted_ean ?? '',
                sku: item.admin_corrected_sku ?? item.extracted_sku ?? '',
                quantity: item.admin_corrected_quantity ?? item.extracted_quantity ?? '',
                unit_price: item.admin_corrected_unit_price ?? item.extracted_unit_price ?? '',
                total_price: item.admin_corrected_total_price ?? item.extracted_total_price ?? '',
                discount: item.admin_corrected_discount ?? item.extracted_discount ?? '',
                tax: item.admin_corrected_tax ?? item.extracted_tax ?? '',
                warranty_duration: item.admin_warranty_duration ?? warrantyResolution?.duration_months ?? '',
                warranty_template_type_id: item.admin_warranty_template_type_id ?? warrantyResolution?.template_type_id ?? '',
                excluded_from_processing: !!item.excluded_from_processing,
                exclusion_note: item.admin_exclusion_note ?? '',
            };
            return acc;
        }, {});
    }, []);

    const hydrateSelectionState = useCallback((fallback, persisted) => {
        if (!persisted || typeof persisted !== 'object') {
            return fallback;
        }

        const linked = persisted.linked && typeof persisted.linked === 'object' ? persisted.linked : fallback.linked;
        const create = persisted.create && typeof persisted.create === 'object'
            ? { ...fallback.create, ...persisted.create }
            : fallback.create;

        return {
            ...fallback,
            mode: normalizeSelectionMode(persisted.mode, linked, create) || fallback.mode,
            linked,
            create,
        };
    }, []);

    const buildPreparedReviewStatePayload = useCallback(() => {
        const persistedCurrency = normalizeCurrencyCodeForSave(adminCurrency, currencyOptions);
        const persistedItemPayloads = new Map(
            (Array.isArray(scan?.items) ? scan.items : [])
                .map((item) => [String(item.id), item?.admin_prepared_review_payload || {}])
        );

        const itemPayloads = Object.entries(itemSelections)
            .filter(([itemId]) => !String(itemId).startsWith('ai-line-'))
            .map(([itemId, selection]) => ({
                scanned_bill_item_id: itemId,
                prepared_review_payload: {
                    manufacturer_selection: selection?.manufacturer || null,
                    product_selection: selection?.product || null,
                    variant_selection: selection?.variant || null,
                    category_resolution: persistedItemPayloads.get(String(itemId))?.category_resolution || null,
                    warranty_resolution: persistedItemPayloads.get(String(itemId))?.warranty_resolution || null,
                    item_edits: itemEdits[itemId] || {},
                },
            }));

        return {
            review_state: {
                notes,
                external_bill_id: externalBillId || null,
                admin_currency: persistedCurrency,
                admin_currency_source: persistedCurrency ? adminCurrencySource : null,
                bill_draft: {
                    billNumber: billDraft.billNumber || null,
                    billDate: billDraft.billDate || null,
                    paymentMethod: billDraft.paymentMethod || null,
                    subtotal: billDraft.subtotal === '' ? null : Number(billDraft.subtotal),
                    taxAmount: billDraft.taxAmount === '' ? null : Number(billDraft.taxAmount),
                    totalAmount: billDraft.totalAmount === '' ? null : Number(billDraft.totalAmount),
                },
                entity_enrichment: entityEnrichment,
                accepted_keys: Array.from(acceptedKeys).sort(),
                retailer_selection: retailerSelection,
                venue_selection: venueSelection,
            },
            items: itemPayloads,
        };
    }, [
        acceptedKeys,
        adminCurrency,
        adminCurrencySource,
        billDraft,
        currencyOptions,
        entityEnrichment,
        externalBillId,
        itemEdits,
        itemSelections,
        notes,
        retailerSelection,
        scan?.items,
        venueSelection,
    ]);

    const loadScan = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchAdminScannedBill(id);
            const persistedReviewState = data.admin_prepared_review_payload || {};
            const persistedItemPayloads = new Map(
                (Array.isArray(data.items) ? data.items : [])
                    .filter((item) => item?.admin_prepared_review_payload)
                    .map((item) => [String(item.id), item.admin_prepared_review_payload])
            );
            setScan(data);
            setNotes(data.admin_review_notes || '');
            setExternalBillId(data.external_bill_id || data.bill_number || '');
            setDuplicateConflict(data.duplicate_conflict || null);
            setAiResult(data.ai_last_response || null);
            setEnrichResult(data.web_enrichment_response || null);
            setBillDraft({
                billNumber: data.admin_bill_number || data.bill_number || '',
                billDate: toDateTimeLocalValue(data.admin_bill_date || data.bill_date),
                paymentMethod: data.admin_payment_method || data.payment_method || '',
                subtotal: data.admin_subtotal ?? data.subtotal ?? '',
                taxAmount: data.admin_tax_amount ?? data.tax_amount ?? '',
                totalAmount: data.admin_total_amount ?? data.total_amount ?? '',
            });

            // Currency seed: reviewed/admin choice is final; AI suggestion stays separate.
            const observedCurrency = data.suggested_currency
                && data.suggested_currency_source === 'observed'
                ? data.suggested_currency
                : (data.ai_last_response?.observed?.currency
                || data.ai_last_response?.extracted?.observed_currency
                || null);
            const inferredCurrency = data.suggested_currency
                && data.suggested_currency_source === 'inferred'
                ? data.suggested_currency
                : (data.ai_last_response?.inferred?.currency
                || data.ai_last_response?.extracted?.inferred_currency
                || null);
            const nextSuggestedCurrency = observedCurrency || inferredCurrency || data.suggested_currency || '';
            const nextSuggestedCurrencySource = data.suggested_currency_source
                || (observedCurrency ? 'observed' : (inferredCurrency ? 'inferred' : 'unknown'));
            setSuggestedCurrency(nextSuggestedCurrency);
            setSuggestedCurrencySource(nextSuggestedCurrency ? nextSuggestedCurrencySource : 'unknown');

            if (data.admin_currency) {
                setAdminCurrency(data.admin_currency);
                setAdminCurrencySource(data.admin_currency_source || 'manual');
            } else if (data.currency) {
                setAdminCurrency(data.currency);
                setAdminCurrencySource('reviewed');
            } else if (observedCurrency) {
                setAdminCurrency(observedCurrency);
                setAdminCurrencySource('observed');
            } else if (inferredCurrency) {
                setAdminCurrency(inferredCurrency);
                setAdminCurrencySource('inferred');
            } else {
                setAdminCurrency('');
                setAdminCurrencySource('unknown');
            }

            const initialItemSelections = buildInitialItemSelections(data);
            const initialItemEdits = buildInitialItemEdits(data);

            Object.entries(initialItemSelections).forEach(([itemId, fallback]) => {
                const persisted = persistedItemPayloads.get(String(itemId));
                if (!persisted || typeof persisted !== 'object') {
                    return;
                }

                initialItemSelections[itemId] = {
                    manufacturer: hydrateSelectionState(fallback.manufacturer, persisted.manufacturer_selection),
                    product: hydrateSelectionState(fallback.product, persisted.product_selection),
                    variant: hydrateSelectionState(fallback.variant, persisted.variant_selection),
                };

                if (persisted.item_edits && typeof persisted.item_edits === 'object') {
                    initialItemEdits[itemId] = {
                        ...initialItemEdits[itemId],
                        ...persisted.item_edits,
                    };
                }
            });

            setItemSelections(initialItemSelections);
            setItemEdits(initialItemEdits);
            setEntityEnrichment({
                retailer: persistedReviewState?.entity_enrichment?.retailer ?? true,
                venue: persistedReviewState?.entity_enrichment?.venue ?? true,
            });
            setAcceptedKeys(new Set(Array.isArray(persistedReviewState?.accepted_keys) ? persistedReviewState.accepted_keys : []));

            // Pre-populate if already linked
            let nextRetailerSelection = {
                mode: null,
                linked: null,
                create: {
                    name: '',
                    brand_name: '',
                    address: '',
                    city: '',
                    country: '',
                    phone: '',
                    email: '',
                    website: '',
                    tax_id: '',
                    vat_id: '',
                    business_type: '',
                }
            };
            if (data.organization) {
                nextRetailerSelection = {
                    mode: 'link',
                    linked: { id: data.organization.id, name: data.organization.name, is_virtual: data.organization.is_virtual },
                    create: {
                        name: '',
                        brand_name: '',
                        address: '',
                        city: '',
                        country: '',
                        phone: '',
                        email: '',
                        website: '',
                        tax_id: '',
                        vat_id: '',
                        business_type: '',
                    }
                };
            } else if (data?.ai_last_response?.extracted?.organization_name) {
                nextRetailerSelection = {
                    ...nextRetailerSelection,
                    mode: nextRetailerSelection.mode || 'create',
                    create: {
                        ...nextRetailerSelection.create,
                        name: data.ai_last_response.extracted.organization_name,
                    },
                };
            }
            nextRetailerSelection = hydrateSelectionState(nextRetailerSelection, persistedReviewState?.retailer_selection);
            setRetailerSelection(nextRetailerSelection);

            let nextVenueSelection = {
                mode: null,
                linked: null,
                create: {
                    name: '',
                    address: '',
                    city: '',
                    country: '',
                    phone: '',
                    website: '',
                    venue_type_id: '',
                }
            };
            if (data.venue) {
                nextVenueSelection = {
                    mode: 'link',
                    linked: { id: data.venue.id, name: data.venue.name, is_virtual: data.venue.is_virtual, address: data.venue.address },
                    create: {
                        name: '',
                        address: '',
                        city: '',
                        country: '',
                        phone: '',
                        website: '',
                        venue_type_id: '',
                    }
                };
            } else if (data?.ai_last_response?.extracted?.venue_name || data?.ai_last_response?.extracted?.venue_address) {
                nextVenueSelection = {
                    ...nextVenueSelection,
                    mode: nextVenueSelection.mode || 'create',
                    create: {
                        ...nextVenueSelection.create,
                        name: data.ai_last_response?.extracted?.venue_name || '',
                        address: data.ai_last_response?.extracted?.venue_address || '',
                    },
                };
            }
            nextVenueSelection = hydrateSelectionState(nextVenueSelection, persistedReviewState?.venue_selection);
            setVenueSelection(nextVenueSelection);

            preparedStateHydratedRef.current = true;
            preparedStateLastSavedRef.current = JSON.stringify({
                review_state: persistedReviewState || {},
                items: Array.from(persistedItemPayloads.entries()).map(([scanned_bill_item_id, prepared_review_payload]) => ({
                    scanned_bill_item_id,
                    prepared_review_payload,
                })),
            });
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [buildInitialItemEdits, buildInitialItemSelections, hydrateSelectionState, id]);

    useEffect(() => { loadScan(); }, [loadScan]);

    useEffect(() => {
        if (!preparedStateHydratedRef.current || !scan?.id) {
            return undefined;
        }

        const payload = buildPreparedReviewStatePayload();
        const serialized = JSON.stringify(payload);

        if (serialized === preparedStateLastSavedRef.current) {
            return undefined;
        }

        if (preparedStateSaveTimerRef.current) {
            clearTimeout(preparedStateSaveTimerRef.current);
        }

        preparedStateSaveTimerRef.current = setTimeout(async () => {
            try {
                await saveAdminScannedBillPreparedReviewState(id, payload);
                preparedStateLastSavedRef.current = serialized;
            } catch (err) {
                console.error('Failed to persist scanned bill prepared review state.', err);
            }
        }, 800);

        return () => {
            if (preparedStateSaveTimerRef.current) {
                clearTimeout(preparedStateSaveTimerRef.current);
            }
        };
    }, [buildPreparedReviewStatePayload, id, scan?.id]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [venueTypes, warrantyTypes, currencies, countries] = await Promise.all([
                    fetchAdminScannedBillVenueTypes(),
                    fetchAdminScannedBillWarrantyTemplateTypes(),
                    fetchCurrencies(),
                    fetchCountryCodes(),
                ]);
                if (!active) return;
                setVenueTypeOptions(Array.isArray(venueTypes) ? venueTypes : []);
                setWarrantyTemplateTypeOptions(Array.isArray(warrantyTypes) ? warrantyTypes : []);
                setCurrencyOptions(Array.isArray(currencies) ? currencies : []);
                setCountryOptions(Array.isArray(countries) ? countries : []);
            } catch (_) {
                if (!active) return;
                setVenueTypeOptions([]);
                setWarrantyTemplateTypeOptions([]);
                setCurrencyOptions([]);
                setCountryOptions([]);
            }
        })();

        return () => {
            active = false;
        };
    }, []);

    // ========================================================================
    // SEARCH FUNCTIONS
    // ========================================================================
    const searchRetailers = useCallback(async (query) => {
        try {
            const data = await searchAdminScannedBillOrganizations({ query, type: 'retailer' });
            setRetailerOptions((data || []).map(o => ({
                value: o.id,
                label: `${o.name} ${o.is_virtual ? '(Virtual)' : '(Real)'}`,
                raw: o,
                isVirtual: o.is_virtual,
            })));
        } catch (_) { setRetailerOptions([]); }
    }, []);

    const searchVenues = useCallback(async (query, orgId) => {
        try {
            const data = await searchAdminScannedBillVenues({ query, organization_id: orgId || undefined });
            setVenueOptions((data || []).map(v => ({
                value: v.id,
                label: `${v.name}${v.address ? ` - ${v.address}` : ''} ${v.is_virtual ? '(Virtual)' : '(Real)'}`,
                raw: v,
                isVirtual: v.is_virtual,
            })));
        } catch (_) { setVenueOptions([]); }
    }, []);

    const searchManufacturers = useCallback(async (itemId, query) => {
        try {
            const data = await searchAdminScannedBillOrganizations({ query, type: 'manufacturer' });
            setManufacturerOptions(prev => ({
                ...prev,
                [itemId]: (data || []).map(o => ({
                    value: o.id,
                    label: `${o.name} ${o.is_virtual ? '(Virtual)' : '(Real)'}`,
                    raw: o,
                    isVirtual: o.is_virtual,
                }))
            }));
        } catch (_) { setManufacturerOptions(prev => ({ ...prev, [itemId]: [] })); }
    }, []);

    const searchProducts = useCallback(async (itemId, query) => {
        try {
            const data = await searchAdminScannedBillManufacturerItems({ query });
            setProductOptions(prev => ({
                ...prev,
                [itemId]: (data || []).map(p => ({
                    value: p.id,
                    label: `${p.product_name}${p.model_number ? ` (${p.model_number})` : ''} ${p.is_virtual ? '(Virtual)' : '(Real)'}`,
                    raw: p,
                    isVirtual: p.is_virtual,
                }))
            }));
        } catch (_) { setProductOptions(prev => ({ ...prev, [itemId]: [] })); }
    }, []);

    const searchVariants = useCallback(async (itemId, productId, query) => {
        if (!productId) { setVariantOptions(prev => ({ ...prev, [itemId]: [] })); return; }
        try {
            const data = await searchAdminScannedBillManufacturerVariants({ manufacturer_item_id: productId, query });
            setVariantOptions(prev => ({
                ...prev,
                [itemId]: (data || []).map(v => ({
                    value: v.id,
                    label: v.sku || v.variant_model_number || v.ean || `Variant ${v.id}`,
                    raw: v,
                    isVirtual: v.is_virtual,
                }))
            }));
        } catch (_) { setVariantOptions(prev => ({ ...prev, [itemId]: [] })); }
    }, []);

    // ========================================================================
    // AI HANDLER
    // ========================================================================
    const handleAiSuggest = async () => {
        try {
            setAiLoading(true);
            setAiError('');
            const response = await suggestAdminScannedBillWithAi(id, {
                apply: true,
                force: forceAiRun,
                use_processed_image: useProcessedImageForAi,
                image_detail: useHighDetailImage ? 'high' : 'auto',
            });

            if (response?.scan) {
                setScan(response.scan);
                setBillDraft({
                    billNumber: response.scan.admin_bill_number || response.scan.bill_number || '',
                    billDate: toDateTimeLocalValue(response.scan.admin_bill_date || response.scan.bill_date),
                    paymentMethod: response.scan.admin_payment_method || response.scan.payment_method || '',
                    subtotal: response.scan.admin_subtotal ?? response.scan.subtotal ?? '',
                    taxAmount: response.scan.admin_tax_amount ?? response.scan.tax_amount ?? '',
                    totalAmount: response.scan.admin_total_amount ?? response.scan.total_amount ?? '',
                });
                setItemEdits(buildInitialItemEdits(response.scan));
            }

            const result = response?.result || null;
            setAiResult(result);

            // Pre-populate selections based on AI matches
            const extracted = result?.extracted || {};
            const matched = result?.matched || {};

            // Retailer
            if (matched.organization?.id) {
                setRetailerSelection({
                    mode: 'link',
                    linked: {
                        id: matched.organization.id,
                        name: matched.organization.name,
                        is_virtual: matched.organization.is_virtual,
                        confidence: matched.organization.confidence
                    },
                    create: { name: extracted.organization_name || '' },
                    candidates: filterRetailerCandidates(matched.organization?.candidates || []),
                });
            } else if (extracted.organization_name) {
                setRetailerSelection({
                    mode: 'create',
                    linked: null,
                    create: { name: extracted.organization_name },
                    candidates: filterRetailerCandidates(matched.organization?.candidates || [])
                });
            }

            // Venue
            if (matched.venue?.id) {
                setVenueSelection({
                    mode: 'link',
                    linked: {
                        id: matched.venue.id,
                        name: matched.venue.name,
                        address: matched.venue.address,
                        is_virtual: matched.venue.is_virtual,
                        confidence: matched.venue.confidence
                    },
                    create: { name: extracted.venue_name || '', address: extracted.venue_address || '' }
                });
            } else if (extracted.venue_name || extracted.venue_address) {
                setVenueSelection({
                    mode: 'create',
                    linked: null,
                    create: { name: extracted.venue_name || '', address: extracted.venue_address || '' },
                    candidates: matched.venue?.candidates || []
                });
            }

            // Items
            const aiItems = result?.matched?.items || [];
            const newSelections = { ...itemSelections };

            (response?.scan?.items || scan?.items || []).forEach(scanItem => {
                const aiItem = aiItems.find(ai => String(ai.line_number) === String(scanItem.line_number));

                if (!newSelections[scanItem.id]) {
                    newSelections[scanItem.id] = {
                        manufacturer: { mode: null, linked: null, create: { name: '' } },
                        product: { mode: null, linked: null, create: { name: '', model_number: '', ean: '' } },
                        variant: { mode: null, linked: null, create: { sku: '' } },
                    };
                }

                // Manufacturer for this item
                if (aiItem?.matched_manufacturer_id) {
                    newSelections[scanItem.id].manufacturer = {
                        mode: 'link',
                        linked: {
                            id: aiItem.matched_manufacturer_id,
                            name: aiItem.matched_manufacturer_name || 'Manufacturer',
                            is_virtual: aiItem.manufacturer_is_virtual
                        },
                        create: { name: aiItem.extracted_brand || aiItem.extracted_manufacturer_name || extracted.manufacturer_name || '' }
                    };
                } else if (aiItem?.extracted_brand || aiItem?.extracted_manufacturer_name || extracted.manufacturer_name) {
                    newSelections[scanItem.id].manufacturer = {
                        mode: 'create',
                        linked: null,
                        create: { name: aiItem.extracted_brand || aiItem.extracted_manufacturer_name || extracted.manufacturer_name || '' }
                    };
                }

                // Product
                if (aiItem?.matched_manufacturer_item_id) {
                    newSelections[scanItem.id].product = {
                        mode: 'link',
                        linked: {
                            id: aiItem.matched_manufacturer_item_id,
                            name: aiItem.matched_manufacturer_item_name || 'Product',
                            is_virtual: aiItem.product_is_virtual,
                            confidence: aiItem.match_confidence_score
                        },
                        create: {
                            name: aiItem.extracted_name || '',
                            model_number: aiItem.extracted_model || '',
                            ean: aiItem.extracted_ean || ''
                        }
                    };
                } else if (aiItem?.extracted_name) {
                    newSelections[scanItem.id].product = {
                        mode: 'create',
                        linked: null,
                        create: {
                            name: aiItem.extracted_name,
                            model_number: aiItem.extracted_model || '',
                            ean: aiItem.extracted_ean || ''
                        }
                    };
                }

                // Variant
                if (aiItem?.matched_variant_id) {
                    newSelections[scanItem.id].variant = {
                        mode: 'link',
                        linked: { id: aiItem.matched_variant_id, sku: aiItem.matched_variant_sku },
                        create: { sku: aiItem.extracted_sku || '' }
                    };
                } else if (aiItem?.extracted_sku) {
                    newSelections[scanItem.id].variant = {
                        mode: 'create',
                        linked: null,
                        create: { sku: aiItem.extracted_sku }
                    };
                }
            });

            setItemSelections(newSelections);

        } catch (err) {
            setAiError(err?.response?.data?.message || 'Failed to run AI analysis.');
        } finally {
            setAiLoading(false);
        }
    };

    // Sve cetiri tablice dolaze iz /show; ako ih nema (stariji racun obradjen
    // prije nego su postojale), sekcija se jednostavno ne iscrtava.
    const aiRuns = useMemo(() => (Array.isArray(scan?.ai_runs) ? scan.ai_runs : []), [scan]);
    const billMatches = useMemo(() => (Array.isArray(scan?.matches) ? scan.matches : []), [scan]);
    const imageDocs = useMemo(() => (Array.isArray(scan?.images) ? scan.images : []), [scan]);
    const aiCost = scan?.ai_cost || null;
    const aiProgress = scan?.ai_progress || null;
    const aiReview = scan?.ai_review || null;

    const scanItems = useMemo(() => {
        const extractedItems = aiResult?.extracted?.items || scan?.ai_last_response?.extracted?.items || [];
        const realItems = Array.isArray(scan?.items) ? scan.items : [];

        if (extractedItems.length > 0) {
            const realByLine = new Map(realItems.map((item) => [String(item.line_number), item]));
            return extractedItems.map((item, index) => {
                const lineNumber = item?.line_number || (index + 1);
                const persisted = realByLine.get(String(lineNumber));
                return {
                    id: persisted?.id || `ai-line-${lineNumber}`,
                    line_number: lineNumber,
                    raw_text: persisted?.admin_corrected_raw_text || item?.line_text || persisted?.raw_text || null,
                    extracted_name: persisted?.admin_corrected_name || item?.name || persisted?.extracted_name || null,
                    extracted_model_number: persisted?.admin_corrected_model_number || item?.model_number || item?.variant_model_number || persisted?.extracted_model_number || null,
                    extracted_ean: persisted?.admin_corrected_ean || item?.ean || persisted?.extracted_ean || null,
                    extracted_sku: persisted?.admin_corrected_sku || item?.sku || item?.organization_sku || persisted?.extracted_sku || null,
                    extracted_quantity: persisted?.admin_corrected_quantity ?? item?.quantity ?? persisted?.extracted_quantity ?? 1,
                    extracted_unit_price: persisted?.admin_corrected_unit_price ?? item?.unit_price ?? persisted?.extracted_unit_price ?? null,
                    extracted_total_price: persisted?.admin_corrected_total_price ?? item?.total_price ?? persisted?.extracted_total_price ?? null,
                    extracted_discount: persisted?.admin_corrected_discount ?? persisted?.extracted_discount ?? item?.discount ?? null,
                    extracted_tax: persisted?.admin_corrected_tax ?? persisted?.extracted_tax ?? item?.tax ?? null,
                    excluded_from_processing: persisted?.excluded_from_processing ?? false,
                    admin_exclusion_note: persisted?.admin_exclusion_note ?? '',
                    matched_manufacturer_id: persisted?.matched_manufacturer_id || null,
                    matched_manufacturer_item_id: persisted?.matched_manufacturer_item_id || null,
                    matched_variant_id: persisted?.matched_variant_id || null,
                    needs_enrichment: persisted?.needs_enrichment ?? true,
                    // Spajanje ispod je popis DOPUSTENIH polja, ne prosirenje.
                    // Sto se ovdje ne navede, ne postoji u prikazu - a garancije
                    // i uparivanja dolaze iz baze, ne iz AI odgovora, pa bi bez
                    // ove tri linije sekcija bila prazna na svakom racunu koji
                    // ima ai_last_response.
                    warranties: persisted?.warranties || [],
                    warranty_state: persisted?.warranty_state ?? null,
                    matches: persisted?.matches || [],
                };
            });
        }

        return realItems.length > 0 ? realItems.map((item) => ({
            ...item,
            needs_enrichment: item?.needs_enrichment ?? true,
        })) : extractedItems.map((item, index) => ({
            id: `ai-line-${index + 1}`,
            line_number: index + 1,
            raw_text: item?.line_text || null,
            extracted_name: item?.name || null,
            extracted_model_number: item?.model_number || item?.variant_model_number || null,
            extracted_ean: item?.ean || null,
            extracted_sku: item?.sku || item?.organization_sku || null,
            extracted_quantity: item?.quantity ?? 1,
            extracted_unit_price: item?.unit_price ?? null,
            extracted_total_price: item?.total_price ?? null,
            matched_manufacturer_id: null,
            matched_manufacturer_item_id: null,
            matched_variant_id: null,
            needs_enrichment: true,
        }));
    }, [aiResult, scan]);

    const enrichmentEligible = useMemo(() => {
        if (!aiResult) return false;
        const retailerNew = entityEnrichment.retailer && (retailerSelection.mode === 'create' || (retailerSelection.linked?.is_virtual === true));
        const venueNew = entityEnrichment.venue && (venueSelection.mode === 'create' || (venueSelection.linked?.is_virtual === true));
        const itemsNeed = scanItems.some((item) => {
            const edit = itemEdits[item.id] || {};
            if (!item.needs_enrichment || edit.excluded_from_processing) {
                return false;
            }
            const sel = itemSelections[item.id] || {};
            const productReal = sel?.product?.mode === 'link' && sel?.product?.linked?.is_virtual !== true && !!sel?.product?.linked?.id;
            const manufacturerReal = sel?.manufacturer?.mode === 'link' && sel?.manufacturer?.linked?.is_virtual !== true && !!sel?.manufacturer?.linked?.id;
            return !productReal || !manufacturerReal || sel?.product?.mode === 'create' || sel?.manufacturer?.mode === 'create';
        });
        return retailerNew || venueNew || itemsNeed;
    }, [aiResult, entityEnrichment, retailerSelection, venueSelection, itemSelections, itemEdits, scanItems]);

    // Build the enrichment payload from the CURRENT admin form state.
    // The user picked "Frontend selection state" as source of truth, so the
    // server receives admin-edited names/models, not the raw AI extraction.
    const buildEnrichPayload = useCallback(() => {
        const isRealLinked = (sel) => sel?.mode === 'link' && sel?.linked?.is_virtual !== true && !!sel?.linked?.id;

        const matchedOrganization = aiResult?.matched?.organization || null;
        const matchedVenue = aiResult?.matched?.venue || null;

        const retailer = entityEnrichment.retailer ? {
            mode: isRealLinked(retailerSelection)
                ? 'existing'
                : (retailerSelection.create?.name || retailerSelection.linked?.is_virtual ? 'create_new' : 'ignore'),
            name: retailerSelection.create?.name || aiResult?.extracted?.organization_name || null,
            original_name: aiResult?.extracted?.original_organization_name || null,
            selected_id: retailerSelection.linked?.id || null,
            address: venueSelection.create?.address || aiResult?.extracted?.venue_address || null,
            extracted_name: aiResult?.extracted?.organization_name || null,
            extracted_original_name: aiResult?.extracted?.original_organization_name || null,
            db_match: matchedOrganization,
            db_candidates: filterRetailerCandidates(matchedOrganization?.candidates || []),
        } : null;

        const venue = entityEnrichment.venue ? {
            mode: isRealLinked(venueSelection)
                ? 'existing'
                : (venueSelection.create?.name || venueSelection.create?.address || venueSelection.linked?.is_virtual ? 'create_new' : 'ignore'),
            name: venueSelection.create?.name || aiResult?.extracted?.venue_name || null,
            original_name: aiResult?.extracted?.original_venue_name || null,
            address: venueSelection.create?.address || aiResult?.extracted?.venue_address || null,
            original_address: aiResult?.extracted?.original_venue_address || null,
            retailer_name: retailerSelection.create?.name || retailerSelection.linked?.name || null,
            selected_id: venueSelection.linked?.id || null,
            extracted_name: aiResult?.extracted?.venue_name || null,
            extracted_address: aiResult?.extracted?.venue_address || null,
            db_match: matchedVenue,
            db_candidates: (matchedVenue?.candidates || []).filter((c) => Number(c?.confidence || 0) >= MIN_DB_MATCH_CONFIDENCE),
        } : null;

        const items = [];

        (scanItems || []).forEach((item) => {
            const sel = itemSelections[item.id] || {};
            const edit = itemEdits[item.id] || {};
            const aiMatchedItem = aiResult?.matched?.items?.find((ai) => String(ai.line_number) === String(item.line_number)) || null;
            const productReal = isRealLinked(sel.product);

            if (!!item.needs_enrichment && !edit.excluded_from_processing && !productReal) {
                items.push({
                    scanned_bill_item_id: item.id,
                    line_number: item.line_number,
                    mode: 'create_new',
                    needs_enrichment: true,
                    name: sel.product?.create?.name || edit.name || item.extracted_name || null,
                    original_name: aiMatchedItem?.extracted_original_name || null,
                    manufacturer_name: sel.manufacturer?.create?.name || aiMatchedItem?.extracted_manufacturer_name || null,
                    brand: sel.manufacturer?.create?.name || aiMatchedItem?.extracted_brand || null,
                    model_number: sel.product?.create?.model_number || edit.model_number || item.extracted_model_number || null,
                    ean: sel.product?.create?.ean || edit.ean || item.extracted_ean || null,
                    sku: edit.sku || item.extracted_sku || null,
                    extracted_name: edit.name || item.extracted_name || null,
                    db_match: aiMatchedItem ? {
                        matched_manufacturer_id: aiMatchedItem.matched_manufacturer_id || null,
                        matched_manufacturer_name: aiMatchedItem.matched_manufacturer_name || null,
                        matched_manufacturer_item_id: aiMatchedItem.matched_manufacturer_item_id || null,
                        matched_manufacturer_item_name: aiMatchedItem.matched_manufacturer_item_name || null,
                        matched_variant_id: aiMatchedItem.matched_variant_id || null,
                        match_confidence_score: aiMatchedItem.match_confidence_score || null,
                        match_method: aiMatchedItem.match_method || null,
                    } : null,
                });
            }
        });

        return {
            scanned_bill_id: id,
            retailer,
            venue,
            manufacturers: [],
            items,
        };
    }, [id, entityEnrichment, retailerSelection, venueSelection, itemSelections, itemEdits, scanItems, aiResult, filterRetailerCandidates]);

    const handleEnrichBill = async () => {
        try {
            setEnrichLoading(true);
            setEnrichError('');
            const payload = buildEnrichPayload();
            const response = await enrichAdminScannedBill(id, {
                force: enrichForce,
                entities: {
                    retailer: !!payload.retailer,
                    venue: !!payload.venue,
                    manufacturers: false,
                    items: (payload.items || []).length > 0,
                },
                payload,
            });
            if (response?.scan) {
                setScan(response.scan);
            }
            setEnrichResult(response?.result || null);
            // Reset accepted markers when fresh results arrive.
            setAcceptedKeys(new Set());
        } catch (err) {
            setEnrichError(err?.response?.data?.message || 'Failed to run web enrichment.');
        } finally {
            setEnrichLoading(false);
        }
    };

    // Accept = pure local state update. Resolved values flow back into the
    // create-new form fields. handleProcess then sends them to bootstrap.
    const handleAcceptEnrichment = useCallback(({ kind, lineNumber, candidateIndex }) => {
        if (!enrichResult) return;

        if (kind === 'retailer') {
            const r = enrichResult.retailer?.result;
            if (!r) return;
            setRetailerSelection((prev) => ({
                ...prev,
                mode: 'create',
                linked: null,
                create: {
                    ...(prev.create || {}),
                    name: r.legal_name || r.brand_name || prev.create?.name || '',
                    brand_name: r.brand_name || prev.create?.brand_name || '',
                    address: r.address || prev.create?.address || '',
                    city: r.city || prev.create?.city || '',
                    country: r.country || prev.create?.country || '',
                    phone: r.support_phone || prev.create?.phone || '',
                    email: r.support_email || prev.create?.email || '',
                    website: r.website || prev.create?.website || '',
                    tax_id: r.tax_id || prev.create?.tax_id || '',
                    vat_id: r.vat_id || prev.create?.vat_id || '',
                    business_type: r.business_type || prev.create?.business_type || '',
                },
            }));
            setAcceptedKeys((prev) => new Set(prev).add('retailer'));
            return;
        }

        if (kind === 'venue') {
            const v = enrichResult.venue?.result;
            if (!v) return;
            setVenueSelection((prev) => ({
                ...prev,
                mode: 'create',
                linked: null,
                create: {
                    ...(prev.create || {}),
                    name: v.resolved_name || prev.create?.name || '',
                    address: v.resolved_address || prev.create?.address || '',
                    city: v.resolved_city || prev.create?.city || '',
                    country: v.resolved_country || prev.create?.country || '',
                    phone: v.phone || prev.create?.phone || '',
                    website: v.website || prev.create?.website || '',
                },
            }));
            setAcceptedKeys((prev) => new Set(prev).add('venue'));
            return;
        }

        if (kind === 'item') {
            const entry = (enrichResult.items || []).find(
                (x) => String(x.line_number) === String(lineNumber)
            );
            if (!entry?.result) return;
            const item = scanItems.find((it) => String(it.line_number) === String(lineNumber));
            if (!item) return;
            const r = entry.result;
            const candidate = Number.isInteger(candidateIndex) ? r.candidates?.[candidateIndex] : null;
            const resolvedName = candidate?.name || r.resolved_product_name || '';
            const resolvedModel = candidate?.model_number || r.resolved_model_number || '';
            const resolvedEan = candidate?.ean || r.resolved_ean || r.resolved_gtin || '';
            const resolvedManufacturer = candidate?.manufacturer || r.resolved_manufacturer || '';
            const resolvedWarrantyDuration = r.resolved_warranty_duration_months;

            setItemSelections((prev) => {
                const current = prev[item.id] || {};
                const next = {
                    ...current,
                    product: {
                        mode: 'create',
                        linked: null,
                        create: {
                            name: resolvedName || current.product?.create?.name || '',
                            model_number: resolvedModel || current.product?.create?.model_number || '',
                            ean: resolvedEan || current.product?.create?.ean || '',
                        },
                    },
                };
                // Also flow manufacturer if we resolved one and admin hasn't linked an existing.
                if (resolvedManufacturer && current.manufacturer?.mode !== 'link') {
                    next.manufacturer = {
                        mode: 'create',
                        linked: null,
                        create: { name: resolvedManufacturer },
                    };
                }
                return { ...prev, [item.id]: next };
            });

            if (resolvedWarrantyDuration) {
                setItemEdits((prev) => ({
                    ...prev,
                    [item.id]: {
                        ...(prev[item.id] || {}),
                        warranty_duration: prev[item.id]?.warranty_duration || String(resolvedWarrantyDuration),
                    },
                }));
            }

            const key = Number.isInteger(candidateIndex)
                ? `item:${lineNumber}:c${candidateIndex}`
                : `item:${lineNumber}`;
            setAcceptedKeys((prev) => new Set(prev).add(key));
        }
    }, [enrichResult, scanItems]);

    const handleReprocessOcr = async () => {
        try {
            setOcrLoading(true);
            setOcrError('');
            const response = await reprocessAdminScannedBill(id, { language: ocrLanguage });

            if (response?.scan) {
                setScan(response.scan);
                setAiResult(null);
                setItemSelections(buildInitialItemSelections(response.scan));
                setItemEdits(buildInitialItemEdits(response.scan));
                setBillDraft({
                    billNumber: response.scan.admin_bill_number || response.scan.bill_number || '',
                    billDate: toDateTimeLocalValue(response.scan.admin_bill_date || response.scan.bill_date),
                    paymentMethod: response.scan.admin_payment_method || response.scan.payment_method || '',
                    subtotal: response.scan.admin_subtotal ?? response.scan.subtotal ?? '',
                    taxAmount: response.scan.admin_tax_amount ?? response.scan.tax_amount ?? '',
                    totalAmount: response.scan.admin_total_amount ?? response.scan.total_amount ?? '',
                });
            }

            setOcrResult(response?.result || null);
        } catch (err) {
            setOcrError(err?.response?.data?.message || 'Failed to rerun backend OCR.');
        } finally {
            setOcrLoading(false);
        }
    };

    // ========================================================================
    // PROCESSING LOGIC
    // ========================================================================
    const handleProcess = async () => {
        try {
            setProcessing(true);
            setProcessError('');

            const hasOnlyPreviewItems = Object.keys(itemSelections).some((itemId) => String(itemId).startsWith('ai-line-'));
            if ((scan?.items?.length || 0) === 0 && hasOnlyPreviewItems) {
                throw new Error('Run AI analysis once to materialize item rows before finalizing.');
            }

            // 1. Persist admin-corrected item fields first so bootstrap/final processing
            // use the reviewed values instead of the raw AI extraction.
            for (const item of scanItems) {
                if (String(item.id).startsWith('ai-line-')) {
                    continue;
                }

                const sel = itemSelections[item.id] || {};
                const edit = itemEdits[item.id] || {};

                await matchAdminScannedBillItem(id, item.id, {
                    matched_manufacturer_id: sel.manufacturer?.mode === 'link' ? sel.manufacturer.linked?.id : null,
                    matched_manufacturer_item_id: sel.product?.mode === 'link' ? sel.product.linked?.id : null,
                    matched_variant_id: sel.variant?.mode === 'link' ? sel.variant.linked?.id : null,
                    match_method: sel.product?.mode === 'link' ? 'admin_manual' : item.match_method || null,
                    admin_corrected_raw_text: edit.raw_text || null,
                    admin_corrected_name: edit.name || null,
                    admin_corrected_model_number: edit.model_number || null,
                    admin_corrected_ean: edit.ean || null,
                    admin_corrected_sku: edit.sku || null,
                    admin_corrected_quantity: edit.quantity === '' ? null : Number(edit.quantity),
                    admin_corrected_unit_price: edit.unit_price === '' ? null : Number(edit.unit_price),
                    admin_corrected_total_price: edit.total_price === '' ? null : Number(edit.total_price),
                    admin_corrected_discount: edit.discount === '' ? null : Number(edit.discount),
                    admin_corrected_tax: edit.tax === '' ? null : Number(edit.tax),
                    admin_warranty_duration: edit.warranty_duration === '' ? null : Number(edit.warranty_duration),
                    admin_warranty_template_type_id: edit.warranty_template_type_id || null,
                    excluded_from_processing: !!edit.excluded_from_processing,
                    admin_exclusion_note: edit.exclusion_note || null,
                    needs_enrichment: !!item.needs_enrichment && !edit.excluded_from_processing,
                });
            }

            // 2. If creating new entities, call bootstrap
            const needsBootstrap =
                retailerSelection.mode === 'create' ||
                venueSelection.mode === 'create' ||
                Object.values(itemSelections).some(item =>
                    item.manufacturer.mode === 'create' ||
                    item.product.mode === 'create'
                );

            if (needsBootstrap) {
                await bootstrapAdminScannedBillEntities(id, {
                    retailer_organization_id: retailerSelection.mode === 'link' ? retailerSelection.linked?.id : null,
                    retailer_organization_name: retailerSelection.mode === 'create' ? retailerSelection.create.name : null,
                    retailer_organization_brand_name: retailerSelection.mode === 'create' ? retailerSelection.create.brand_name : null,
                    retailer_organization_address: retailerSelection.mode === 'create' ? retailerSelection.create.address : null,
                    retailer_organization_city: retailerSelection.mode === 'create' ? retailerSelection.create.city : null,
                    retailer_organization_country: retailerSelection.mode === 'create' ? retailerSelection.create.country : null,
                    retailer_organization_phone: retailerSelection.mode === 'create' ? retailerSelection.create.phone : null,
                    retailer_organization_email: retailerSelection.mode === 'create' ? retailerSelection.create.email : null,
                    retailer_organization_website: retailerSelection.mode === 'create' ? retailerSelection.create.website : null,
                    retailer_organization_tax_id: retailerSelection.mode === 'create' ? retailerSelection.create.tax_id : null,
                    retailer_organization_vat_id: retailerSelection.mode === 'create' ? retailerSelection.create.vat_id : null,
                    retailer_organization_business_type: retailerSelection.mode === 'create' ? retailerSelection.create.business_type : null,
                    retailer_venue_id: venueSelection.mode === 'link' ? venueSelection.linked?.id : null,
                    retailer_venue_name: venueSelection.mode === 'create' ? venueSelection.create.name : null,
                    retailer_venue_address: venueSelection.mode === 'create' ? venueSelection.create.address : null,
                    retailer_venue_city: venueSelection.mode === 'create' ? venueSelection.create.city : null,
                    retailer_venue_country: venueSelection.mode === 'create' ? venueSelection.create.country : null,
                    retailer_venue_phone: venueSelection.mode === 'create' ? venueSelection.create.phone : null,
                    retailer_venue_website: venueSelection.mode === 'create' ? venueSelection.create.website : null,
                    retailer_venue_type_id: venueSelection.mode === 'create' && venueSelection.create.venue_type_id !== '' ? Number(venueSelection.create.venue_type_id) : null,
                    items: Object.entries(itemSelections).map(([itemId, sel]) => ({
                        scanned_bill_item_id: itemId,
                        manufacturer_id: sel.manufacturer.mode === 'link' ? sel.manufacturer.linked?.id : null,
                        manufacturer_name: sel.manufacturer.mode === 'create' ? sel.manufacturer.create.name : null,
                        product_id: sel.product.mode === 'link' ? sel.product.linked?.id : null,
                        product_name: sel.product.mode === 'create' ? sel.product.create.name : null,
                        product_model_number: sel.product.mode === 'create' ? sel.product.create.model_number : null,
                        product_ean: sel.product.mode === 'create' ? sel.product.create.ean : null,
                        product_warranty_duration: itemEdits[itemId]?.warranty_duration === '' ? null : Number(itemEdits[itemId]?.warranty_duration),
                        warranty_template_type_id: itemEdits[itemId]?.warranty_template_type_id || null,
                        variant_id: sel.variant.mode === 'link' ? sel.variant.linked?.id : null,
                        variant_sku: sel.variant.mode === 'create' ? sel.variant.create.sku : null,
                    })),
                    default_warranty_duration: 24,
                });
            }

            // 3. Persist resolved bill-level review fields while staying in admin_review
            await reviewAdminScannedBill(id, {
                status: 'admin_review',
                admin_review_notes: notes,
                external_bill_id: externalBillId || null,
                organization_id: retailerSelection.linked?.id || null,
                venue_id: venueSelection.linked?.id || null,
                admin_currency: adminCurrency || null,
                admin_currency_source: adminCurrency ? adminCurrencySource : null,
                admin_bill_number: billDraft.billNumber || null,
                admin_bill_date: billDraft.billDate || null,
                admin_payment_method: billDraft.paymentMethod || null,
                admin_subtotal: billDraft.subtotal === '' ? null : Number(billDraft.subtotal),
                admin_tax_amount: billDraft.taxAmount === '' ? null : Number(billDraft.taxAmount),
                admin_total_amount: billDraft.totalAmount === '' ? null : Number(billDraft.totalAmount),
            });

            // 4. Finalize through canonical backend pipeline
            await finalizeAdminScannedBill(id, {
                notes,
                external_bill_id: externalBillId || null,
            });

            // 5. Reload and show success
            await loadScan();

        } catch (err) {
            const conflict = err?.response?.data?.conflict || null;
            if (conflict) {
                setDuplicateConflict(conflict);
            }
            const conflictMessage = conflict
                ? buildDuplicateMessage(conflict)
                : null;
            setProcessError(conflictMessage || err?.response?.data?.message || 'Failed to process bill.');
        } finally {
            setProcessing(false);
        }
    };

    const handleGeminiPrepareReview = async () => {
        try {
            setGeminiPrepareLoading(true);
            setGeminiPrepareError('');

            await prepareAdminScannedBillReviewWithGemini(id, {
                use_processed_image: useProcessedImageForAi,
            });

            await loadScan();
        } catch (err) {
            setGeminiPrepareError(
                err?.response?.data?.message
                || err?.message
                || 'Failed to run Gemini prepared review pipeline.'
            );
        } finally {
            setGeminiPrepareLoading(false);
        }
    };

    const buildDuplicateMessage = (conflict) => {
        if (!conflict) return null;
        if (conflict.type === 'bill') {
            return `Duplicate bill detected: external bill "${conflict.external_bill_id}" already exists for this retailer (Bill ID: ${conflict.bill_id}).`;
        }
        if (conflict.type === 'scanned_bill') {
            return `Duplicate scan detected: external bill "${conflict.external_bill_id}" already scanned for this retailer (Scan ID: ${conflict.scanned_bill_id}, status: ${conflict.status}).`;
        }
        return 'Duplicate bill detected for this retailer and external bill number.';
    };

    // ========================================================================
    // COMPUTED VALUES
    // ========================================================================
    const originalBillImages = useMemo(() => {
        if (!scan) return [];

        return Array.isArray(scan.images)
            ? scan.images.filter((img) => (img?.variant_type || 'original') !== 'processed')
            : [];
    }, [scan]);

    const processedBillImage = useMemo(() => {
        if (!scan || !Array.isArray(scan.images)) return null;
        return scan.images.find((img) => img?.variant_type === 'processed') || null;
    }, [scan]);

    const originalBillImageUrls = useMemo(() => {
        const urls = [];
        const pushIf = (val) => {
            if (typeof val === 'string' && val.trim()) urls.push(val.trim());
        };

        originalBillImages.forEach((img) => {
            if (typeof img === 'string') {
                pushIf(img);
                return;
            }
            pushIf(img?.url);
            pushIf(img?.image_url);
            pushIf(img?.public_url);
            pushIf(img?.path_url);
        });

        if (urls.length === 0) {
            pushIf(scan?.image_url);
            pushIf(scan?.file_url);
            pushIf(scan?.original_image_url);
        }

        return Array.from(new Set(urls));
    }, [originalBillImages, scan]);

    const verificationRawText = useMemo(() => {
        if (!scan) return '';

        const direct =
            scan.ocr_raw_text ||
            scan.raw_ocr_text ||
            scan.extracted_raw_text ||
            scan.raw_text ||
            scan.ocr_text;

        if (typeof direct === 'string' && direct.trim()) return direct;

        return (scan.items || [])
            .map((item) => item?.raw_text)
            .filter(Boolean)
            .join('\n');
    }, [scan]);

    const processedPreviewUrl = useMemo(() => {
        if (ocrResult?.processed_image?.preview_data_url) {
            return ocrResult.processed_image.preview_data_url;
        }

        if (aiResult?.vision?.preview_data_url) {
            return aiResult.vision.preview_data_url;
        }

        if (processedBillImage?.image_url) {
            return processedBillImage.image_url;
        }

        if (processedBillImage?.url) {
            return processedBillImage.url;
        }

        if (scan?.ai_last_response?.vision?.preview_data_url) {
            return scan.ai_last_response.vision.preview_data_url;
        }

        return null;
    }, [aiResult, ocrResult, processedBillImage, scan]);

    // Calculate what will happen when processing
    const processingPreview = useMemo(() => {
        const retailerIsReal = retailerSelection.mode === 'link' && !retailerSelection.linked?.is_virtual;
        const willCreateRetailer = retailerSelection.mode === 'create';
        const willCreateVenue = venueSelection.mode === 'create';

        const itemsPreview = Object.entries(itemSelections).map(([itemId, sel]) => {
            const scanItem = scanItems.find(i => i.id === itemId);
            const mfrIsReal = sel.manufacturer.mode === 'link' && !sel.manufacturer.linked?.is_virtual;
            const productIsNew = sel.product.mode === 'create' || (sel.product.mode === 'link' && sel.product.linked?.is_virtual);

            return {
                itemId,
                name: scanItem?.extracted_name || 'Unknown Item',
                manufacturerIsReal: mfrIsReal,
                manufacturerName: sel.manufacturer.linked?.name || sel.manufacturer.create?.name || 'Unknown',
                productIsNew,
                productName: sel.product.linked?.name || sel.product.create?.name || 'Unknown',
                needsMfrApproval: mfrIsReal && productIsNew,
            };
        });

        return {
            retailerIsReal,
            willCreateRetailer,
            willCreateVenue,
            willGoToRetailerQueue: retailerIsReal,
            itemsNeedingMfrApproval: itemsPreview.filter(i => i.needsMfrApproval),
            allVirtual: !retailerIsReal && itemsPreview.every(i => !i.manufacturerIsReal),
            itemsPreview,
        };
    }, [retailerSelection, venueSelection, itemSelections, scanItems]);

    // Select styles
    const selectStyles = useMemo(() => ({
        control: (base, state) => ({
            ...base,
            borderRadius: radius.md,
            borderColor: state.isFocused ? colors.primary : colors.border,
            minHeight: 42,
            boxShadow: state.isFocused ? `0 0 0 2px ${colors.primaryLight}` : 'none',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? colors.primary : state.isFocused ? colors.primaryLight : colors.surface,
            color: state.isSelected ? colors.surface : colors.text,
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }), []);

    // ========================================================================
    // RENDER
    // ========================================================================
    if (loading) return <LoadingMessage>{t('ScannedBills.loading')}</LoadingMessage>;
    if (error || !scan) return <ErrorMessage>{t('ScannedBills.error')}</ErrorMessage>;

    const isProcessedView = scan.status === 'processed';
    const isRejectedView = scan.status === 'rejected' || scan.status === 'retailer_rejected';
    const retailerDisplayName = retailerSelection.linked?.name || retailerSelection.create?.name || '-';
    const venueDisplayName = venueSelection.linked?.name || venueSelection.create?.name || '-';

    return (
        <PageContainer>
            <PageHeader>
                <HeaderRow>
                    <BackButton onClick={() => navigate('/admin/scanned-bills')}>
                        &larr; {t('Common.back')}
                    </BackButton>
                    <PageTitle>{t('AdminScannedBills.detail.pageTitle')}</PageTitle>
                    {/* Sirovi naziv stanja je bio ono sto administrator cita:
                        "admin_review". Naziv iz sifrarnika nije recenica. */}
                    <StatusBadge $status={scan.status}>
                        {t(`ScannedBills.statuses.${scan.status}`, { defaultValue: scan.status })}
                    </StatusBadge>
                </HeaderRow>
            </PageHeader>

            <PageContent>
                {/* Presuda AI-a, prije svega ostalog.
                    Administrator je do sada vidio DA racun ceka pregled, ali ne
                    i zasto - razlog je bio izracunat i spremljen, a nigdje se
                    nije prikazivao, pa je otvorio racun i sam trazio sto ne
                    valja. Kad je racun vec obradjen, ovo je zapis sto je AI
                    mislio u trenutku odluke. */}
                {aiReview && (
                    <VerdictBanner $ok={aiReview.auto_approve}>
                        <VerdictMark>{aiReview.auto_approve ? '✓' : '!'}</VerdictMark>
                        <VerdictBody>
                            {aiReview.auto_approve ? (
                                <strong>
                                    {isProcessedView
                                        ? 'AI nije imao primjedbi na ovaj racun.'
                                        : 'AI bi ovo odobrio — nista ne trazi tvoju odluku.'}
                                </strong>
                            ) : (
                                <>
                                    <strong>Treba tvoj pregled:</strong>
                                    <ul>
                                        {asList(aiReview.reasons).map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                </>
                            )}
                            <VerdictMeta>
                                {aiReview.match_count} uparivanja, od toga {aiReview.proof_count} s
                                provjerljivim dokazom
                                {aiReview.created_count > 0 && `, ${aiReview.created_count} novih zapisa`}
                            </VerdictMeta>
                        </VerdictBody>
                    </VerdictBanner>
                )}

                {/* Bill Info Header */}
                <BillInfoCard>
                    <BillInfoGrid>
                        <BillInfoItem>
                            <label>{t('Common.user')}</label>
                            <span>{scan.user?.email || '-'}</span>
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>Bill Number</label>
                            <TextInput
                                value={billDraft.billNumber}
                                disabled={isProcessedView}
                                onChange={(e) => setBillDraft((prev) => ({ ...prev, billNumber: e.target.value }))}
                                placeholder={scan.bill_number || 'Enter bill number'}
                            />
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>Bill Date</label>
                            <TextInput
                                type="datetime-local"
                                value={billDraft.billDate}
                                disabled={isProcessedView}
                                onChange={(e) => setBillDraft((prev) => ({ ...prev, billDate: e.target.value }))}
                            />
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>{t('Common.items')}</label>
                            <span>{scanItems.length}</span>
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>Payment Method</label>
                            <TextInput
                                value={billDraft.paymentMethod}
                                disabled={isProcessedView}
                                onChange={(e) => setBillDraft((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                                placeholder={scan.payment_method || 'cash / card / other'}
                            />
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>Subtotal</label>
                            <TextInput
                                value={billDraft.subtotal}
                                disabled={isProcessedView}
                                onChange={(e) => setBillDraft((prev) => ({ ...prev, subtotal: e.target.value }))}
                                placeholder={scan.subtotal ?? '0.00'}
                            />
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>Tax Amount</label>
                            <TextInput
                                value={billDraft.taxAmount}
                                disabled={isProcessedView}
                                onChange={(e) => setBillDraft((prev) => ({ ...prev, taxAmount: e.target.value }))}
                                placeholder={scan.tax_amount ?? '0.00'}
                            />
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>{t('Common.total')}</label>
                            <TextInput
                                value={billDraft.totalAmount}
                                disabled={isProcessedView}
                                onChange={(e) => setBillDraft((prev) => ({ ...prev, totalAmount: e.target.value }))}
                                placeholder={scan.total_amount ?? '0.00'}
                            />
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>External Bill Number</label>
                            <TextInput
                                value={externalBillId}
                                disabled={isProcessedView}
                                onChange={(e) => {
                                    setExternalBillId(e.target.value);
                                    if (duplicateConflict) setDuplicateConflict(null);
                                }}
                                placeholder={scan.bill_number || 'Enter external bill number'}
                            />
                        </BillInfoItem>
                        <BillInfoItem>
                            <label>
                                Currency
                                {adminCurrencySource && adminCurrencySource !== 'unknown' && (
                                    <CurrencySourceBadge $source={adminCurrencySource}>
                                        {adminCurrencySource}
                                    </CurrencySourceBadge>
                                )}
                            </label>
                            <SimpleSelect
                                value={adminCurrency}
                                disabled={isProcessedView}
                                options={currencyOptions.map((currency) => {
                                    const code = currency.code || currency.key || '';
                                    return {
                                        value: code,
                                        label: `${code}${currency.name ? ` - ${currency.name}` : ''}`,
                                    };
                                })}
                                placeholder="Select currency"
                                onChange={(_, value) => {
                                    setAdminCurrency(value);
                                    setAdminCurrencySource('manual');
                                }}
                            />
                            {suggestedCurrency && (
                                <MutedText style={{ marginTop: 6 }}>
                                    AI suggestion: <strong>{suggestedCurrency}</strong>
                                    {suggestedCurrencySource && suggestedCurrencySource !== 'unknown'
                                        ? ` (${suggestedCurrencySource})`
                                        : ''}
                                </MutedText>
                            )}
                        </BillInfoItem>
                    </BillInfoGrid>
                    {duplicateConflict && (
                        <ErrorBanner style={{ marginTop: 12 }}>
                            {buildDuplicateMessage(duplicateConflict)}
                        </ErrorBanner>
                    )}
                </BillInfoCard>

                {/* AI Analysis Section */}
                {!isProcessedView && (
                <Section>
                    <SectionHeader>
                        <SectionTitle>{t('AdminScannedBills.detail.sections.aiAnalysis')}</SectionTitle>
                        {scan.ai_last_processed_at && (
                            <CacheHint>{t('AdminScannedBills.detail.cachedAt')}: {new Date(scan.ai_last_processed_at).toLocaleString()}</CacheHint>
                        )}
                    </SectionHeader>
                    <SectionBody>
                        <AIActions>
                            <PrimaryButton onClick={handleAiSuggest} disabled={aiLoading}>
                                {aiLoading ? t('AdminScannedBills.detail.ai.analyzing') : t('AdminScannedBills.detail.ai.runAnalysis')}
                            </PrimaryButton>
                            <SecondaryButton type="button" onClick={() => setShowVerifyModal(true)}>
                                Verify Bill
                            </SecondaryButton>
                            <CheckboxLabel>
                                <input type="checkbox" checked={forceAiRun} onChange={e => setForceAiRun(e.target.checked)} />
                                {t('AdminScannedBills.detail.ai.forceRerun')}
                            </CheckboxLabel>
                            <CheckboxLabel>
                                <input
                                    type="checkbox"
                                    checked={useProcessedImageForAi}
                                    onChange={e => setUseProcessedImageForAi(e.target.checked)}
                                />
                                Use processed image for AI
                            </CheckboxLabel>
                            <CheckboxLabel title="High detail costs more — use only for difficult receipts.">
                                <input
                                    type="checkbox"
                                    checked={useHighDetailImage}
                                    onChange={e => setUseHighDetailImage(e.target.checked)}
                                />
                                Use high detail image
                            </CheckboxLabel>
                        </AIActions>
                        {aiError && <ErrorBanner>{aiError}</ErrorBanner>}
                        {aiResult && (
                            <AIResultBanner>
                                {t('AdminScannedBills.detail.ai.extractedBanner', {
                                    items: aiResult?.extracted?.items?.length || 0,
                                    matched: aiResult?.summary?.matched_items || 0,
                                    total: aiResult?.summary?.total_items || 0
                                })}
                                {aiResult?.vision?.used ? ` Processed image sent to AI.${aiResult?.vision?.processed ? ' OCR-enhanced preview was used.' : ''}` : ''}
                            </AIResultBanner>
                        )}
                    </SectionBody>
                </Section>
                )}

                {/* Sto je AI vidjelo i na temelju cega je odlucilo.
                    Do sada je od ovoga bio vidljiv samo krajnji rezultat, pa se
                    kriva odluka nije dala razlikovati od tocne dok se racun ne
                    prodje rucno. */}
                {(aiRuns.length > 0 || billMatches.length > 0 || imageDocs.length > 0) && (
                <Section>
                    <SectionHeader>
                        <SectionTitle>Obrada i dokazi</SectionTitle>
                        {aiCost?.total_tokens ? (
                            <CacheHint>{formatTokens(aiCost.total_tokens)} tokena ukupno</CacheHint>
                        ) : null}
                    </SectionHeader>
                    <SectionBody>

                        {/* Sto je od obrade gotovo, a sto ne. Izlaz svake zavrsene
                            faze je u bazi, pa pad na petom koraku ne placa prva
                            cetiri ponovo. */}
                        {aiProgress && (
                            <>
                                <StageName>Tijek obrade</StageName>
                                <ProgressBar>
                                    {asList(aiProgress.stages).map(st => {
                                        const done = asList(aiProgress.completed).includes(st);
                                        const stageRow = asList(aiCost?.by_stage).find(x => x.stage === st);
                                        const skipped = !done && stageRow?.status === 'skipped';
                                        const isNext = aiProgress.next_stage === st;
                                        return (
                                            <ProgressStep
                                                key={st}
                                                $state={done ? 'done' : skipped ? 'skipped' : isNext ? 'next' : 'todo'}
                                            >
                                                {done ? '✓' : skipped ? '–' : isNext ? '▸' : '·'} {stageLabel(st)}
                                            </ProgressStep>
                                        );
                                    })}
                                </ProgressBar>
                                {aiProgress.next_stage && (
                                    <EmptyHint style={{ marginTop: -8, marginBottom: 14 }}>
                                        Nastavlja se od faze „{stageLabel(aiProgress.next_stage)}" — prethodne se ne racunaju ponovo.
                                    </EmptyHint>
                                )}
                            </>
                        )}

                        {/* Kadar: sto je na slici i da li se da procitati */}
                        {imageDocs.length > 0 && (
                            <>
                                <StageName>Dokumenti u kadru</StageName>
                                <EvidenceTable style={{ marginBottom: 16 }}>
                                    {imageDocs.map(img => (
                                        <div key={img.id}>
                                            <EvidenceRow>
                                                <strong>{docTypeLabel(img.document_type)}</strong>
                                                <span>{[img.language, img.script].filter(Boolean).join(' / ') || '—'}</span>
                                                <span>
                                                    {asList(img.documents).length > 1
                                                        ? `${asList(img.documents).length} dokumenta u istom kadru: ` +
                                                          asList(img.documents).map(d => docTypeLabel(d.type)).join(', ')
                                                        : (asList(img.documents)[0]?.issuer_name || '')}
                                                </span>
                                                {/* Tri stanja, ne dva: iznosi se citaju, ne citaju,
                                                    ili ih dokument uopce ne printa. Otpremnica je
                                                    treci slucaj i nije nikakav nedostatak slike. */}
                                                {img.amounts_legible === null ? (
                                                    <span style={{ fontSize: type.label, color: colors.textMuted }}>
                                                        dokument ne printa iznose
                                                    </span>
                                                ) : (
                                                    <ProofBadge $proof={img.amounts_legible !== false}>
                                                        {img.amounts_legible === false ? 'iznosi neprocitljivi' : 'iznosi citljivi'}
                                                    </ProofBadge>
                                                )}
                                            </EvidenceRow>
                                            {asList(img.capture_issues).length > 0 && (
                                                <div style={{ padding: '0.375rem 0.625rem 0' }}>
                                                    {asList(img.capture_issues).map(i => (
                                                        <CaptureIssue key={i}>{captureIssueLabel(i)}</CaptureIssue>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Nova fotografija je pitanje za onoga tko je slikao.
                                                Podatak koji nije u kadru se ne da ispraviti pregledom. */}
                                            {img.needs_retake === true && (
                                                <RetakeBanner>
                                                    Treba nova fotografija — ovo se ne moze ispraviti pregledom.
                                                </RetakeBanner>
                                            )}
                                        </div>
                                    ))}
                                </EvidenceTable>
                            </>
                        )}

                        {/* Uparivanja: dokaz naspram slicnosti */}
                        {billMatches.length > 0 && (
                            <>
                                <StageName>Uparivanje s bazom</StageName>
                                <EvidenceTable style={{ marginBottom: 16 }}>
                                    {asList(billMatches).map(m => (
                                        <EvidenceRow key={m.id}>
                                            <strong>{targetTypeLabel(m.target_type)}</strong>
                                            <span>{decisionLabel(m.decision)}</span>
                                            <span style={{ color: colors.textSecondary }}>
                                                {m.reason || m.evidence?.label || '—'}
                                            </span>
                                            {/* Provjerljiva cinjenica i slicnost imena nisu ista vrsta
                                                tvrdnje, a do sada su obje izlazile kao isti postotak. */}
                                            <ProofBadge $proof={!!m.is_proof}>
                                                {m.is_proof ? 'dokaz' : 'procjena'}
                                            </ProofBadge>
                                        </EvidenceRow>
                                    ))}
                                </EvidenceTable>
                            </>
                        )}

                        {/* Potrosnja po fazi */}
                        {aiRuns.length > 0 && (
                            <>
                                <StageName>Potrosnja po fazi</StageName>
                                <StageGrid>
                                    {asList(aiCost?.by_stage).map(st => (
                                        <StageCard key={st.stage} $failed={st.failed > 0}>
                                            <StageName>{stageLabel(st.stage)}</StageName>
                                            <StageTokens>{formatTokens(st.input_tokens + st.output_tokens)}</StageTokens>
                                            <StageDetail>
                                                ulaz {formatTokens(st.input_tokens)} · izlaz {formatTokens(st.output_tokens)}
                                            </StageDetail>
                                            {st.runs > 1 && (
                                                <StageDetail>{st.runs} pokretanja (po fotografiji)</StageDetail>
                                            )}
                                            {st.retries > 0 && (
                                                <StageDetail>{st.retries} ponovnih pokusaja{st.failed > 0 ? `, ${st.failed} palo` : ''}</StageDetail>
                                            )}
                                        </StageCard>
                                    ))}
                                </StageGrid>
                            </>
                        )}

                    </SectionBody>
                </Section>
                )}

                {/* Retailer Organization */}
                <Section>
                    <SectionHeader>
                        <SectionTitle>{t('AdminScannedBills.detail.sections.retailerOrganization')}</SectionTitle>
                        {retailerSelection.linked && (
                            <EntityTypeBadge $isVirtual={retailerSelection.linked.is_virtual}>
                                {retailerSelection.linked.is_virtual ? t('Common.virtual').toUpperCase() : t('Common.real').toUpperCase()}
                            </EntityTypeBadge>
                        )}
                    </SectionHeader>
                    <SectionBody>
                        {aiResult?.extracted?.organization_name && (
                            <ExtractedData>
                                <label>{t('Common.extracted')}:</label> {aiResult.extracted.organization_name}
                            </ExtractedData>
                        )}
                        {!isProcessedView && (retailerSelection.mode === 'create' || retailerSelection.linked?.is_virtual) && (
                            <NeedsEnrichmentLabel style={{ marginBottom: 12 }}>
                                <input
                                    type="checkbox"
                                    checked={!!entityEnrichment.retailer}
                                    onChange={(e) => setEntityEnrichment((prev) => ({ ...prev, retailer: e.target.checked }))}
                                />
                                Enrich retailer
                            </NeedsEnrichmentLabel>
                        )}

                        {/* Candidates from AI */}
                        {retailerSelection.candidates?.length > 0 && !retailerSelection.linked && (
                            <CandidatesBox>
                                <CandidatesTitle>{t('AdminScannedBills.detail.possibleMatches')}</CandidatesTitle>
                                {retailerSelection.candidates.slice(0, 3).map(c => (
                                    <CandidateRow key={c.id}>
                                        <CandidateInfo>
                                            <span className="name">{c.name}</span>
                                            <EntityTypeBadge $isVirtual={c.is_virtual} $small>
                                                {c.is_virtual ? t('Common.virtual') : t('Common.real')}
                                            </EntityTypeBadge>
                                            <span className="confidence">{Math.round((c.confidence || 0) * 100)}%</span>
                                        </CandidateInfo>
                                        <SmallButton onClick={() => setRetailerSelection({
                                            ...retailerSelection,
                                            mode: 'link',
                                            linked: { id: c.id, name: c.name, is_virtual: c.is_virtual }
                                        })}>
                                            {t('Common.select')}
                                        </SmallButton>
                                    </CandidateRow>
                                ))}
                            </CandidatesBox>
                        )}

                        {/* Current selection */}
                        {retailerSelection.linked && (
                            <SelectedBox $isVirtual={retailerSelection.linked.is_virtual}>
                                <SelectedInfo>
                                    <strong>{t('Common.selected')}:</strong> {retailerSelection.linked.name}
                                    {retailerSelection.linked.confidence && (
                                        <span className="confidence">({Math.round(retailerSelection.linked.confidence * 100)}% match)</span>
                                    )}
                                </SelectedInfo>
                                {!isProcessedView && (
                                    <SmallButton onClick={() => setRetailerSelection({ mode: null, linked: null, create: retailerSelection.create })}>
                                        {t('Common.change')}
                                    </SmallButton>
                                )}
                            </SelectedBox>
                        )}

                        {isProcessedView && !retailerSelection.linked && (
                            <SelectedBox $isVirtual={true}>
                                <SelectedInfo>
                                    <strong>{t('Common.selected')}:</strong> {retailerDisplayName}
                                </SelectedInfo>
                            </SelectedBox>
                        )}

                        {/* Link or Create options */}
                        {!isProcessedView && !retailerSelection.linked && (
                            <OptionsGrid>
                                <OptionCard
                                    $active={retailerSelection.mode === 'link'}
                                    onClick={() => setRetailerSelection({ ...retailerSelection, mode: 'link' })}
                                >
                                    <OptionTitle>{t('AdminScannedBills.detail.linkToExisting')}</OptionTitle>
                                    <OptionDesc>{t('AdminScannedBills.detail.retailerSearchDesc')}</OptionDesc>
                                    {retailerSelection.mode === 'link' && (
                                        <OptionContent>
                                            <SearchableSelect
                                                styles={selectStyles}
                                                menuPortalTarget={document.body}
                                                placeholder={t('AdminScannedBills.detail.placeholders.searchOrganizations')}
                                                options={retailerOptions}
                                                onMenuOpen={() => searchRetailers(aiResult?.extracted?.organization_name || '')}
                                                onInputChange={(v, m) => {
                                                    if (m.action === 'input-change') searchRetailers(v);
                                                    return v;
                                                }}
                                                onChange={(_, __, opt) => opt && setRetailerSelection({
                                                    ...retailerSelection,
                                                    linked: { id: opt.value, name: opt.raw.name, is_virtual: opt.raw.is_virtual }
                                                })}
                                                formatOptionLabel={opt => (
                                                    <OptionLabelRow>
                                                        <span>{opt.raw?.name || opt.label}</span>
                                                        <EntityTypeBadge $isVirtual={opt.isVirtual} $small>
                                                            {opt.isVirtual ? t('Common.virtual') : t('Common.real')}
                                                        </EntityTypeBadge>
                                                    </OptionLabelRow>
                                                )}
                                            />
                                        </OptionContent>
                                    )}
                                </OptionCard>

                                <OptionCard
                                    $active={retailerSelection.mode === 'create'}
                                    onClick={() => setRetailerSelection({ ...retailerSelection, mode: 'create' })}
                                >
                                    <OptionTitle>{t('AdminScannedBills.detail.createNewVirtual')}</OptionTitle>
                                    <OptionDesc>{t('AdminScannedBills.detail.createVirtualOrganizationDesc')}</OptionDesc>
                                    {retailerSelection.mode === 'create' && (
                                        <OptionContent>
                                            <TextInput
                                                placeholder={t('AdminScannedBills.detail.placeholders.organizationName')}
                                                value={retailerSelection.create.name}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, name: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="Brand name"
                                                value={retailerSelection.create.brand_name}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, brand_name: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder={t('Common.address')}
                                                value={retailerSelection.create.address}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, address: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="City"
                                                value={retailerSelection.create.city}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, city: e.target.value }
                                                })}
                                            />
                                            <div onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
                                                <CountrySelect
                                                    countries={countryOptions}
                                                    value={retailerSelection.create.country}
                                                    valueField="name"
                                                    includeIsoInLabel
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                    placeholder="Country"
                                                    onChange={(_, nextValue) => setRetailerSelection((prev) => ({
                                                        ...prev,
                                                        create: { ...prev.create, country: nextValue || '' }
                                                    }))}
                                                />
                                            </div>
                                            <TextInput
                                                placeholder="Phone"
                                                value={retailerSelection.create.phone}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, phone: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="Email"
                                                value={retailerSelection.create.email}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, email: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="Website"
                                                value={retailerSelection.create.website}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, website: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="Tax ID"
                                                value={retailerSelection.create.tax_id}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, tax_id: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="VAT ID"
                                                value={retailerSelection.create.vat_id}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, vat_id: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="Business type"
                                                value={retailerSelection.create.business_type}
                                                onChange={e => setRetailerSelection({
                                                    ...retailerSelection,
                                                    create: { ...retailerSelection.create, business_type: e.target.value }
                                                })}
                                            />
                                        </OptionContent>
                                    )}
                                </OptionCard>
                            </OptionsGrid>
                        )}
                    </SectionBody>
                </Section>

                <StandardModal
                    isOpen={showVerifyModal}
                    onClose={() => setShowVerifyModal(false)}
                    title="Verify Bill Scan"
                    width="min(1200px, 96vw)"
                >
                    <VerifyModalGrid>
                        <VerifyColumn>
                            <VerifyBlock>
                                <VerifyBlockTitle>Bill Image</VerifyBlockTitle>
                                        {originalBillImageUrls.length > 0 ? (
                                            <ImageList>
                                                {originalBillImageUrls.map((url, idx) => (
                                                    <ImageCard key={`${url}-${idx}`}>
                                                        <BillImagePreview src={url} alt={`Bill ${idx + 1}`} />
                                                        <ImageLink href={url} target="_blank" rel="noreferrer">
                                                            Open original
                                                        </ImageLink>
                                                    </ImageCard>
                                                ))}
                                            </ImageList>
                                        ) : (
                                            <MutedText>No bill image URL available in this response.</MutedText>
                                        )}
                                    </VerifyBlock>

                                    <VerifyBlock>
                                        <VerifyBlockTitle>Processed Receipt Preview</VerifyBlockTitle>
                                        {processedPreviewUrl ? (
                                            <ImageCard>
                                                <BillImagePreview src={processedPreviewUrl} alt="Processed bill preview" />
                                                {processedBillImage?.image_url && (
                                                    <ImageLink href={processedBillImage.image_url} target="_blank" rel="noreferrer">
                                                        Open processed
                                                    </ImageLink>
                                                )}
                                            </ImageCard>
                                        ) : (
                                            <MutedText>Run backend OCR or AI with processed image to preview the cleaned receipt.</MutedText>
                                        )}
                                    </VerifyBlock>

                            <VerifyBlock>
                                <VerifyBlockTitle>Raw OCR / Extracted Lines</VerifyBlockTitle>
                                <OcrToolbar>
                                    <OcrField>
                                        <label>Backend OCR language</label>
                                        <select
                                            value={ocrLanguage}
                                            onChange={(event) => setOcrLanguage(event.target.value)}
                                            disabled={ocrLoading}
                                        >
                                            <option value="auto">Auto: Macedonian + Serbian + Croatian + English</option>
                                            <option value="mkd">Macedonian Cyrillic</option>
                                            <option value="srp">Serbian Cyrillic</option>
                                            <option value="latin">Latin / Croatian / English</option>
                                            <option value="eng">English only</option>
                                        </select>
                                    </OcrField>
                                        <SecondaryButton
                                            type="button"
                                            onClick={handleReprocessOcr}
                                            disabled={ocrLoading || originalBillImageUrls.length === 0}
                                        >
                                            {ocrLoading ? 'Running OCR...' : 'Rerun Backend OCR'}
                                        </SecondaryButton>
                                </OcrToolbar>
                                {ocrError && <ErrorBanner>{ocrError}</ErrorBanner>}
                                {ocrResult && (
                                    <AIResultBanner>
                                        Backend OCR updated raw text with {ocrResult.tesseract_languages} ({ocrResult.text?.length || 0} chars).
                                    </AIResultBanner>
                                )}
                                {verificationRawText ? (
                                    <CodeBlock>{verificationRawText}</CodeBlock>
                                ) : (
                                    <MutedText>No raw OCR text found on scan payload.</MutedText>
                                )}
                            </VerifyBlock>
                        </VerifyColumn>

                        <VerifyColumn>
                            <VerifyBlock>
                                <VerifyBlockTitle>Items (Raw + Selected)</VerifyBlockTitle>
                                <VerifyItemsList>
                                    {scanItems.map((item) => {
                                        const sel = itemSelections[item.id] || {};
                                        return (
                                            <VerifyItemRow key={item.id}>
                                                <div><strong>Line {item.line_number || '-'}</strong></div>
                                                <div>{item.raw_text || item.extracted_name || '-'}</div>
                                                <div>
                                                    <small>
                                                        Selected manufacturer: {sel.manufacturer?.linked?.name || sel.manufacturer?.create?.name || '-'}
                                                    </small>
                                                </div>
                                                <div>
                                                    <small>
                                                        Selected product: {sel.product?.linked?.name || sel.product?.create?.name || '-'}
                                                    </small>
                                                </div>
                                                <div>
                                                    <small>
                                                        Selected variant/SKU: {sel.variant?.linked?.sku || sel.variant?.create?.sku || item.extracted_sku || '-'}
                                                    </small>
                                                </div>
                                            </VerifyItemRow>
                                        );
                                    })}
                                </VerifyItemsList>
                            </VerifyBlock>

                            <VerifyBlock>
                                <VerifyBlockTitle>AI Response (Raw)</VerifyBlockTitle>
                                <CodeBlock>{JSON.stringify(aiResult || scan?.ai_last_response || null, null, 2)}</CodeBlock>
                            </VerifyBlock>
                        </VerifyColumn>
                    </VerifyModalGrid>
                </StandardModal>

                {/* Venue */}
                <Section>
                    <SectionHeader>
                        <SectionTitle>{t('AdminScannedBills.detail.sections.venue')}</SectionTitle>
                        {venueSelection.linked && (
                            <EntityTypeBadge $isVirtual={venueSelection.linked.is_virtual}>
                                {venueSelection.linked.is_virtual ? t('Common.virtual').toUpperCase() : t('Common.real').toUpperCase()}
                            </EntityTypeBadge>
                        )}
                    </SectionHeader>
                    <SectionBody>
                        {(aiResult?.extracted?.venue_name || aiResult?.extracted?.venue_address) && (
                            <ExtractedData>
                                <label>{t('Common.extracted')}:</label> {aiResult.extracted.venue_name} {aiResult.extracted.venue_address && `- ${aiResult.extracted.venue_address}`}
                            </ExtractedData>
                        )}
                        {!isProcessedView && (venueSelection.mode === 'create' || venueSelection.linked?.is_virtual || venueSelection.create?.address) && (
                            <NeedsEnrichmentLabel style={{ marginBottom: 12 }}>
                                <input
                                    type="checkbox"
                                    checked={!!entityEnrichment.venue}
                                    onChange={(e) => setEntityEnrichment((prev) => ({ ...prev, venue: e.target.checked }))}
                                />
                                Enrich venue
                            </NeedsEnrichmentLabel>
                        )}

                        {venueSelection.linked && (
                            <SelectedBox $isVirtual={venueSelection.linked.is_virtual}>
                                <SelectedInfo>
                                    <strong>{t('Common.selected')}:</strong> {venueSelection.linked.name} {venueSelection.linked.address && `- ${venueSelection.linked.address}`}
                                </SelectedInfo>
                                {!isProcessedView && (
                                    <SmallButton onClick={() => setVenueSelection({ mode: null, linked: null, create: venueSelection.create })}>
                                        {t('Common.change')}
                                    </SmallButton>
                                )}
                            </SelectedBox>
                        )}

                        {isProcessedView && !venueSelection.linked && (
                            <SelectedBox $isVirtual={true}>
                                <SelectedInfo>
                                    <strong>{t('Common.selected')}:</strong> {venueDisplayName}
                                    {venueSelection.create?.address ? ` - ${venueSelection.create.address}` : ''}
                                </SelectedInfo>
                            </SelectedBox>
                        )}

                        {!isProcessedView && !venueSelection.linked && (
                            <OptionsGrid>
                                <OptionCard
                                    $active={venueSelection.mode === 'link'}
                                    onClick={() => setVenueSelection({ ...venueSelection, mode: 'link' })}
                                >
                                    <OptionTitle>{t('AdminScannedBills.detail.linkToExisting')}</OptionTitle>
                                    <OptionDesc>{t('AdminScannedBills.detail.venueSearchDesc')}</OptionDesc>
                                    {venueSelection.mode === 'link' && (
                                        <OptionContent>
                                            <SearchableSelect
                                                styles={selectStyles}
                                                menuPortalTarget={document.body}
                                                placeholder={t('AdminScannedBills.detail.placeholders.searchVenues')}
                                                options={venueOptions}
                                                onMenuOpen={() => searchVenues(aiResult?.extracted?.venue_name || '', retailerSelection.linked?.id)}
                                                onInputChange={(v, m) => {
                                                    if (m.action === 'input-change') searchVenues(v, retailerSelection.linked?.id);
                                                    return v;
                                                }}
                                                onChange={(_, __, opt) => opt && setVenueSelection({
                                                    ...venueSelection,
                                                    linked: { id: opt.value, name: opt.raw.name, address: opt.raw.address, is_virtual: opt.raw.is_virtual }
                                                })}
                                            />
                                        </OptionContent>
                                    )}
                                </OptionCard>

                                <OptionCard
                                    $active={venueSelection.mode === 'create'}
                                    onClick={() => setVenueSelection({ ...venueSelection, mode: 'create' })}
                                >
                                    <OptionTitle>{t('AdminScannedBills.detail.createNewVirtual')}</OptionTitle>
                                    <OptionDesc>{t('AdminScannedBills.detail.createVirtualVenueDesc')}</OptionDesc>
                                    {venueSelection.mode === 'create' && (
                                        <OptionContent>
                                            <TextInput
                                                placeholder={t('AdminScannedBills.detail.placeholders.venueName')}
                                                value={venueSelection.create.name}
                                                onChange={e => setVenueSelection({
                                                    ...venueSelection,
                                                    create: { ...venueSelection.create, name: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder={t('Common.address')}
                                                value={venueSelection.create.address}
                                                onChange={e => setVenueSelection({
                                                    ...venueSelection,
                                                    create: { ...venueSelection.create, address: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="City"
                                                value={venueSelection.create.city}
                                                onChange={e => setVenueSelection({
                                                    ...venueSelection,
                                                    create: { ...venueSelection.create, city: e.target.value }
                                                })}
                                            />
                                            <div onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
                                                <CountrySelect
                                                    countries={countryOptions}
                                                    value={venueSelection.create.country}
                                                    valueField="name"
                                                    includeIsoInLabel
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                    placeholder="Country"
                                                    onChange={(_, nextValue) => setVenueSelection((prev) => ({
                                                        ...prev,
                                                        create: { ...prev.create, country: nextValue || '' }
                                                    }))}
                                                />
                                            </div>
                                            <TextInput
                                                placeholder="Phone"
                                                value={venueSelection.create.phone}
                                                onChange={e => setVenueSelection({
                                                    ...venueSelection,
                                                    create: { ...venueSelection.create, phone: e.target.value }
                                                })}
                                            />
                                            <TextInput
                                                placeholder="Website"
                                                value={venueSelection.create.website}
                                                onChange={e => setVenueSelection({
                                                    ...venueSelection,
                                                    create: { ...venueSelection.create, website: e.target.value }
                                                })}
                                            />
                                            <SimpleSelect
                                                value={venueSelection.create.venue_type_id}
                                                options={venueTypeOptions.map((opt) => ({
                                                    value: String(opt.id),
                                                    label: opt.name,
                                                }))}
                                                placeholder="Venue type"
                                                onChange={(_, value) => setVenueSelection({
                                                    ...venueSelection,
                                                    create: { ...venueSelection.create, venue_type_id: value }
                                                })}
                                            />
                                        </OptionContent>
                                    )}
                                </OptionCard>
                            </OptionsGrid>
                        )}
                    </SectionBody>
                </Section>

                {/* Items */}
                <Section>
                    <SectionHeader>
                        <SectionTitle>{t('AdminScannedBills.detail.sections.items', { count: scanItems.length })}</SectionTitle>
                    </SectionHeader>
                    <SectionBody>
                        {scanItems.map(item => {
                            const sel = itemSelections[item.id] || {};
                            const edit = itemEdits[item.id] || {};
                            const mfrOpts = manufacturerOptions[item.id] || [];
                            const prodOpts = productOptions[item.id] || [];
                            const aiMatchedItem = aiResult?.matched?.items?.find(ai => String(ai.line_number) === String(item.line_number));
                            const warrantyResolution = item?.admin_prepared_review_payload?.warranty_resolution || {};

                            return (
                                <ItemCard key={item.id}>
                                    <ItemHeader>
                                        <ItemName>{edit.name || item.extracted_name || t('AdminScannedBills.detail.unknownItem')}</ItemName>
                                        {(item.extracted_unit_price || item.unit_price) && (
                                            <ItemPrice>{formatResolvedAmount({
                                                eurAmount: edit.unit_price || item.extracted_unit_price || item.unit_price,
                                                localAmount: item.extracted_unit_price_local || item.unit_price_local,
                                                localCurrencyId: item.currency_id || scan?.currency_id,
                                            })}</ItemPrice>
                                        )}
                                    </ItemHeader>

                                    <ItemMeta>
                                        {(edit.sku || item.extracted_sku) && <span>SKU: {edit.sku || item.extracted_sku}</span>}
                                        {(edit.ean || item.extracted_ean) && <span>EAN: {edit.ean || item.extracted_ean}</span>}
                                        {!isProcessedView && (
                                        <NeedsEnrichmentLabel>
                                            <input
                                                type="checkbox"
                                                checked={!!item.needs_enrichment && !edit.excluded_from_processing}
                                                disabled={!!edit.excluded_from_processing}
                                                onChange={async (e) => {
                                                    const nextChecked = e.target.checked;
                                                    try {
                                                        const updatedItem = await matchAdminScannedBillItem(id, item.id, {
                                                            needs_enrichment: nextChecked,
                                                        });
                                                        setScan((prev) => prev ? {
                                                            ...prev,
                                                            items: (prev.items || []).map((it) =>
                                                                it.id === item.id
                                                                    ? { ...it, ...updatedItem, needs_enrichment: updatedItem?.needs_enrichment ?? nextChecked }
                                                                    : it
                                                            ),
                                                        } : prev);
                                                    } catch (err) {
                                                        setEnrichError(err?.response?.data?.message || 'Failed to update enrichment flag.');
                                                    }
                                                }}
                                            />
                                            Mark for enrichment
                                        </NeedsEnrichmentLabel>
                                        )}
                                        {!isProcessedView && (
                                        <NeedsEnrichmentLabel>
                                            <input
                                                type="checkbox"
                                                checked={!!edit.excluded_from_processing}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: {
                                                        ...(prev[item.id] || {}),
                                                        excluded_from_processing: e.target.checked,
                                                        exclusion_note: e.target.checked
                                                            ? ((prev[item.id]?.exclusion_note || '').trim() || 'Excluded from warranty/import processing - item does not have warranty coverage.')
                                                            : (prev[item.id]?.exclusion_note || ''),
                                                    },
                                                }))}
                                            />
                                            Exclude from warranty/import
                                        </NeedsEnrichmentLabel>
                                        )}
                                    </ItemMeta>

                                    {/* Garancija je popis prava, ne broj. Jedne naocale nose 12
                                        mjeseci od proizvodjaca i 24 od trgovca istovremeno; prikazati
                                        samo jedno znaci kupcu reci krivo. */}
                                    {asList(item.warranties).length > 0 && (
                                        <WarrantyList>
                                            {asList(item.warranties).map(w => {
                                                const voided = w.applies === false && w.kind !== 'not_applicable';
                                                return (
                                                    <React.Fragment key={w.id}>
                                                        <WarrantyRow $effective={!!w.is_effective} $voided={voided}>
                                                            <WarrantyMonths $effective={!!w.is_effective}>
                                                                {w.months == null ? '—' : `${w.months} mj`}
                                                            </WarrantyMonths>
                                                            <WarrantyKind>
                                                                {warrantyKindLabel(w.kind)}
                                                                {w.against_name ? ` · ${w.against_name}` : ''}
                                                            </WarrantyKind>
                                                            {w.is_effective && <ProofBadge $proof>vrijedi</ProofBadge>}
                                                            {voided && <ProofBadge>ne vrijedi ovdje</ProofBadge>}
                                                        </WarrantyRow>
                                                        {w.note && <WarrantyNote>{w.note}</WarrantyNote>}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </WarrantyList>
                                    )}

                                    {item.warranty_state === 'not_established' && (
                                        <EmptyHint>
                                            Trajanje garancije nije utvrdjeno — nije isto kao da je nema.
                                        </EmptyHint>
                                    )}

                                    {!isProcessedView && (
                                    <ItemEditGrid>
                                        <ItemEditField>
                                            <label>Raw Line</label>
                                            <CreateInlineInput
                                                value={edit.raw_text ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), raw_text: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Name</label>
                                            <CreateInlineInput
                                                value={edit.name ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), name: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Model</label>
                                            <CreateInlineInput
                                                value={edit.model_number ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), model_number: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>EAN</label>
                                            <CreateInlineInput
                                                value={edit.ean ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), ean: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>SKU</label>
                                            <CreateInlineInput
                                                value={edit.sku ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), sku: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Qty</label>
                                            <CreateInlineInput
                                                value={edit.quantity ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), quantity: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Unit Price</label>
                                            <CreateInlineInput
                                                value={edit.unit_price ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), unit_price: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Total</label>
                                            <CreateInlineInput
                                                value={edit.total_price ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), total_price: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Discount</label>
                                            <CreateInlineInput
                                                value={edit.discount ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), discount: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Tax</label>
                                            <CreateInlineInput
                                                value={edit.tax ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), tax: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField style={{ gridColumn: '1 / -1' }}>
                                            <label>Exclusion Note</label>
                                            <CreateInlineInput
                                                value={edit.exclusion_note ?? ''}
                                                disabled={!edit.excluded_from_processing}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), exclusion_note: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Warranty Duration (months)</label>
                                            <CreateInlineInput
                                                value={edit.warranty_duration ?? ''}
                                                onChange={(e) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), warranty_duration: e.target.value },
                                                }))}
                                            />
                                        </ItemEditField>
                                        <ItemEditField>
                                            <label>Warranty Template Type</label>
                                            <SimpleSelect
                                                value={edit.warranty_template_type_id ?? ''}
                                                options={warrantyTemplateTypeOptions.map((opt) => ({
                                                    value: String(opt.id),
                                                    label: opt.name,
                                                }))}
                                                placeholder="Select template type"
                                                onChange={(_, value) => setItemEdits((prev) => ({
                                                    ...prev,
                                                    [item.id]: { ...(prev[item.id] || {}), warranty_template_type_id: value },
                                                }))}
                                            />
                                            {warrantyResolution?.action === 'use_existing' && (
                                                <MutedText style={{ marginTop: 6 }}>
                                                    Found template: {warrantyResolution.existing_template_name || 'Warranty template'}
                                                    {warrantyResolution.template_type_name ? ` • ${warrantyResolution.template_type_name}` : ''}
                                                    {warrantyResolution.duration_months ? ` • ${warrantyResolution.duration_months} months` : ''}
                                                </MutedText>
                                            )}
                                            {warrantyResolution?.action === 'create_new' && (
                                                <MutedText style={{ marginTop: 6 }}>
                                                    {isProcessedView
                                                        ? 'Nije bilo aktivnog predloska — koristen je standardni.'
                                                        : 'Nema aktivnog predloska. Napravit ce se standardni.'}
                                                    {warrantyResolution.duration_months ? ` • ${warrantyResolution.duration_months} months` : ''}
                                                </MutedText>
                                            )}
                                        </ItemEditField>
                                    </ItemEditGrid>
                                    )}

                                    {aiMatchedItem && (
                                        <MatchedInfoBox>
                                            {aiMatchedItem.matched_manufacturer_name && (
                                                <span>
                                                    <strong>{t('Common.manufacturer')}:</strong> {aiMatchedItem.matched_manufacturer_name}
                                                </span>
                                            )}
                                            {aiMatchedItem.matched_manufacturer_item_name && (
                                                <span>
                                                    <strong>{t('Common.product')}:</strong> {aiMatchedItem.matched_manufacturer_item_name}
                                                </span>
                                            )}
                                            {aiMatchedItem.match_method && (
                                                <span>
                                                    <strong>Match:</strong> {aiMatchedItem.match_method}
                                                </span>
                                            )}
                                        </MatchedInfoBox>
                                    )}

                                    {isProcessedView && (
                                        <MatchedInfoBox>
                                            <span><strong>Name:</strong> {edit.name || item.extracted_name || '-'}</span>
                                            {(edit.model_number || item.extracted_model_number) && (
                                                <span><strong>Model:</strong> {edit.model_number || item.extracted_model_number}</span>
                                            )}
                                            {(edit.warranty_duration || warrantyResolution?.duration_months) && (() => {
                                                const shown = Number(edit.warranty_duration || warrantyResolution?.duration_months);
                                                const effective = asList(item.warranties).find(w => w.is_effective);
                                                // Ne prepisujemo tiho jedan broj drugim. Ako se razilaze,
                                                // administrator mora vidjeti OBA i zasto - Hailo daje pet
                                                // godina, ali samo unutar EU, pa izvan nje taj broj obecava
                                                // pokrice koje nitko ne duguje.
                                                const disagrees = effective && effective.months != null
                                                    && Number(effective.months) !== shown;
                                                return (
                                                    <>
                                                        <span><strong>Warranty:</strong> {shown} months</span>
                                                        {disagrees && (
                                                            <span style={{ color: colors.warning.text }}>
                                                                <strong>⚠ v3 kaze {effective.months} mj</strong>
                                                                {' — '}{warrantyKindLabel(effective.kind)}
                                                                {effective.against_name ? ` prema ${effective.against_name}` : ''}
                                                            </span>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                            {warrantyResolution?.action === 'use_existing' && (
                                                <span><strong>Template:</strong> {warrantyResolution.existing_template_name || 'Existing template'}</span>
                                            )}
                                            {warrantyResolution?.action === 'create_new' && (
                                                <span><strong>Template:</strong> {isProcessedView ? 'standardni predlozak' : 'standardni predlozak ce se napraviti'}</span>
                                            )}
                                            {item.excluded_from_processing && (
                                                <span><strong>Excluded:</strong> {edit.exclusion_note || item.admin_exclusion_note || 'Yes'}</span>
                                            )}
                                        </MatchedInfoBox>
                                    )}

                                    {!isProcessedView && (
                                    <ItemFields>
                                        {/* Manufacturer */}
                                        <ItemField>
                                            <ItemFieldLabel>
                                                {t('Common.manufacturer')}
                                                {sel.manufacturer?.linked && (
                                                    <EntityTypeBadge $isVirtual={sel.manufacturer.linked.is_virtual} $small>
                                                        {sel.manufacturer.linked.is_virtual ? t('Common.virtual') : t('Common.real')}
                                                    </EntityTypeBadge>
                                                )}
                                            </ItemFieldLabel>
                                            {sel.manufacturer?.linked ? (
                                                <SelectedInline>
                                                    <span>{sel.manufacturer.linked.name}</span>
                                                    <SmallButton onClick={() => setItemSelections(prev => ({
                                                        ...prev,
                                                        [item.id]: { ...prev[item.id], manufacturer: { mode: null, linked: null, create: sel.manufacturer.create } }
                                                    }))}>
                                                        {t('Common.change')}
                                                    </SmallButton>
                                                </SelectedInline>
                                            ) : (
                                                <ItemFieldOptions>
                                                    <SearchableSelect
                                                        styles={selectStyles}
                                                        menuPortalTarget={document.body}
                                                        placeholder={t('AdminScannedBills.detail.placeholders.searchOrCreateManufacturer')}
                                                        options={mfrOpts}
                                                        onMenuOpen={() => searchManufacturers(item.id, sel.manufacturer?.create?.name || item.extracted_name || '')}
                                                        onInputChange={(v, m) => {
                                                            if (m.action === 'input-change') searchManufacturers(item.id, v);
                                                            return v;
                                                        }}
                                                        onChange={(_, __, opt) => opt && setItemSelections(prev => ({
                                                            ...prev,
                                                            [item.id]: {
                                                                ...prev[item.id],
                                                                manufacturer: { mode: 'link', linked: { id: opt.value, name: opt.raw.name, is_virtual: opt.raw.is_virtual }, create: sel.manufacturer?.create || {} }
                                                            }
                                                        }))}
                                                    />
                                                    <CreateInlineInput
                                                        placeholder={t('AdminScannedBills.detail.placeholders.createManufacturer')}
                                                        value={sel.manufacturer?.create?.name || ''}
                                                        onChange={e => setItemSelections(prev => ({
                                                            ...prev,
                                                            [item.id]: {
                                                                ...prev[item.id],
                                                                manufacturer: { mode: 'create', linked: null, create: { name: e.target.value } }
                                                            }
                                                        }))}
                                                    />
                                                </ItemFieldOptions>
                                            )}
                                        </ItemField>

                                        {/* Product */}
                                        <ItemField>
                                            <ItemFieldLabel>
                                                {t('Common.product')}
                                                {sel.product?.linked && (
                                                    <EntityTypeBadge $isVirtual={sel.product.linked.is_virtual} $small>
                                                        {sel.product.linked.is_virtual ? t('Common.virtual') : t('Common.real')}
                                                    </EntityTypeBadge>
                                                )}
                                            </ItemFieldLabel>
                                            {sel.product?.linked ? (
                                                <SelectedInline>
                                                    <span>{sel.product.linked.name}</span>
                                                    {sel.product.linked.confidence && (
                                                        <ConfidenceBadge>{Math.round(sel.product.linked.confidence * 100)}%</ConfidenceBadge>
                                                    )}
                                                    <SmallButton onClick={() => setItemSelections(prev => ({
                                                        ...prev,
                                                        [item.id]: { ...prev[item.id], product: { mode: null, linked: null, create: sel.product.create } }
                                                    }))}>
                                                        {t('Common.change')}
                                                    </SmallButton>
                                                </SelectedInline>
                                            ) : (
                                                <ItemFieldOptions>
                                                    <SearchableSelect
                                                        styles={selectStyles}
                                                        menuPortalTarget={document.body}
                                                        placeholder={t('AdminScannedBills.detail.placeholders.searchProducts')}
                                                        options={prodOpts}
                                                        onMenuOpen={() => searchProducts(item.id, item.extracted_name || '')}
                                                        onInputChange={(v, m) => {
                                                            if (m.action === 'input-change') searchProducts(item.id, v);
                                                            return v;
                                                        }}
                                                        onChange={(_, __, opt) => opt && setItemSelections(prev => ({
                                                            ...prev,
                                                            [item.id]: {
                                                                ...prev[item.id],
                                                                product: { mode: 'link', linked: { id: opt.value, name: opt.raw.product_name, is_virtual: opt.raw.is_virtual }, create: sel.product?.create || {} }
                                                            }
                                                        }))}
                                                    />
                                                    <CreateInlineInput
                                                        placeholder={t('AdminScannedBills.detail.placeholders.createProduct')}
                                                        value={sel.product?.create?.name || ''}
                                                        onChange={e => setItemSelections(prev => ({
                                                            ...prev,
                                                            [item.id]: {
                                                                ...prev[item.id],
                                                                product: { mode: 'create', linked: null, create: { ...sel.product?.create, name: e.target.value } }
                                                            }
                                                        }))}
                                                    />
                                                </ItemFieldOptions>
                                            )}
                                        </ItemField>
                                    </ItemFields>
                                    )}
                                </ItemCard>
                            );
                        })}
                    </SectionBody>
                </Section>

                {/* Processing Preview */}
                <Section>
                    <SectionHeader>
                        <SectionTitle>{isProcessedView ? 'Processed Summary' : t('AdminScannedBills.detail.sections.processingSummary')}</SectionTitle>
                    </SectionHeader>
                    <SectionBody>
                        <PreviewBox>
                            {/* Buduce vrijeme na racunu koji je vec obradjen govori
                                nesto sto nije istina. Isti podaci, druga recenica. */}
                            <PreviewTitle>
                                {isProcessedView
                                    ? 'Sto se dogodilo'
                                    : t('AdminScannedBills.detail.processing.whatWillHappen')}
                            </PreviewTitle>

                            <PreviewList>
                                {/* Retailer */}
                                <PreviewItem>
                                    <PreviewIcon>{processingPreview.retailerIsReal ? '🏢' : '📝'}</PreviewIcon>
                                    <PreviewText>
                                        <strong>{t('Common.retailer')}:</strong> {retailerSelection.linked?.name || retailerSelection.create?.name || t('AdminScannedBills.detail.notSelected')}
                                        {processingPreview.willCreateRetailer && <PreviewTag $type="create">{isProcessedView ? 'Napravljen virtualni' : t('AdminScannedBills.detail.processing.willCreateVirtual')}</PreviewTag>}
                                        {processingPreview.retailerIsReal && <PreviewTag $type="real">{t('AdminScannedBills.detail.processing.realNeedsApproval')}</PreviewTag>}
                                        {retailerSelection.linked?.is_virtual && <PreviewTag $type="virtual">{t('Common.virtual')}</PreviewTag>}
                                    </PreviewText>
                                </PreviewItem>

                                {/* Venue */}
                                <PreviewItem>
                                    <PreviewIcon>📍</PreviewIcon>
                                    <PreviewText>
                                        <strong>{t('Common.venue')}:</strong> {venueSelection.linked?.name || venueSelection.create?.name || t('AdminScannedBills.detail.notSelected')}
                                        {processingPreview.willCreateVenue && <PreviewTag $type="create">{isProcessedView ? 'Napravljena virtualna' : t('AdminScannedBills.detail.processing.willCreateVirtual')}</PreviewTag>}
                                    </PreviewText>
                                </PreviewItem>

                                {/* Items summary */}
                                {processingPreview.itemsPreview.map(item => (
                                    <PreviewItem key={item.itemId}>
                                        <PreviewIcon>📦</PreviewIcon>
                                        <PreviewText>
                                            <strong>{item.name}</strong>
                                            <br />
                                            <small>
                                                {item.manufacturerName} → {item.productName}
                                                {item.needsMfrApproval && <PreviewTag $type="approval">{t('AdminScannedBills.detail.processing.needsManufacturerApproval')}</PreviewTag>}
                                            </small>
                                        </PreviewText>
                                    </PreviewItem>
                                ))}
                            </PreviewList>

                            <PreviewDivider />

                            <PreviewOutcome>
                                {processingPreview.allVirtual ? (
                                    <OutcomeBox $type="virtual">
                                        <OutcomeIcon>📄</OutcomeIcon>
                                        <OutcomeText>
                                            <strong>{t('AdminScannedBills.detail.processing.allVirtualTitle')}</strong>
                                            <p>{t('AdminScannedBills.detail.processing.allVirtualDesc')}</p>
                                        </OutcomeText>
                                    </OutcomeBox>
                                ) : processingPreview.willGoToRetailerQueue ? (
                                    <OutcomeBox $type="approval">
                                        <OutcomeIcon>⏳</OutcomeIcon>
                                        <OutcomeText>
                                            <strong>{t('AdminScannedBills.detail.processing.retailerQueueTitle')}</strong>
                                            <p>{t('AdminScannedBills.detail.processing.retailerQueueDesc')}</p>
                                            {processingPreview.itemsNeedingMfrApproval.length > 0 && (
                                                <p><strong>{t('AdminScannedBills.detail.processing.productsCount', { count: processingPreview.itemsNeedingMfrApproval.length })}</strong> {t('AdminScannedBills.detail.processing.manufacturerVerificationSuffix')}</p>
                                            )}
                                        </OutcomeText>
                                    </OutcomeBox>
                                ) : null}
                            </PreviewOutcome>
                        </PreviewBox>

                        {/* Notes */}
                        {!isProcessedView && (
                        <NotesField>
                            <label>{t('AdminScannedBills.detail.adminNotesOptional')}</label>
                            <TextArea
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder={t('AdminScannedBills.detail.placeholders.adminNotes')}
                                rows={3}
                            />
                        </NotesField>
                        )}

                        {processError && <ErrorBanner>{processError}</ErrorBanner>}

                        {!isProcessedView && (
                        <EnrichSection>
                            <EnrichHeader>
                                <strong>Web Enrichment</strong>
                                <span>
                                    Optional. Researches new retailers / venues / products from public sources.
                                    Skips entities already linked to an existing record.
                                </span>
                            </EnrichHeader>
                            <EnrichActions>
                                <SecondaryButton
                                    type="button"
                                    onClick={handleEnrichBill}
                                    disabled={enrichLoading || !enrichmentEligible}
                                    title={!enrichmentEligible ? 'Run AI analysis and mark at least one entity as create-new first.' : ''}
                                >
                                    {enrichLoading ? 'Enriching…' : 'Enrich Bill'}
                                </SecondaryButton>
                                <CheckboxLabel>
                                    <input
                                        type="checkbox"
                                        checked={enrichForce}
                                        onChange={(e) => setEnrichForce(e.target.checked)}
                                    />
                                    Bypass cache (force fresh search)
                                </CheckboxLabel>
                            </EnrichActions>
                            {enrichError && <ErrorBanner>{enrichError}</ErrorBanner>}

                            {enrichResult && (
                                <EnrichResultsPanel>
                                    <EnrichResultsHeader>
                                        <strong>Enrichment Results</strong>
                                        <small>
                                            Cache hits: {enrichResult.cache_hits ?? 0} · misses: {enrichResult.cache_misses ?? 0}
                                            {enrichResult.usage_total?.total_tokens
                                                ? ` · tokens: ${enrichResult.usage_total.total_tokens}`
                                                : ''}
                                        </small>
                                    </EnrichResultsHeader>
                                    <MutedText style={{ marginBottom: 4 }}>
                                        Accept populates the create-new form fields. You can still edit before Process Bill.
                                    </MutedText>

                                    {enrichResult.retailer && (
                                        <EnrichEntityCard>
                                            <EnrichEntityTitle>
                                                Retailer{enrichResult.retailer.cached ? ' (cached)' : ''}
                                                {acceptedKeys.has('retailer') && <AcceptedMark> ✓ accepted</AcceptedMark>}
                                            </EnrichEntityTitle>
                                            <small>
                                                Proposed: {enrichResult.retailer.result?.legal_name
                                                    || enrichResult.retailer.result?.brand_name
                                                    || '—'}
                                            </small>
                                            <pre>{JSON.stringify(enrichResult.retailer.result, null, 2)}</pre>
                                            <EnrichEntityActions>
                                                <SmallButton
                                                    onClick={() => handleAcceptEnrichment({ kind: 'retailer' })}
                                                >
                                                    {acceptedKeys.has('retailer') ? 'Re-apply' : 'Accept'}
                                                </SmallButton>
                                            </EnrichEntityActions>
                                        </EnrichEntityCard>
                                    )}

                                    {enrichResult.venue && (
                                        <EnrichEntityCard>
                                            <EnrichEntityTitle>
                                                Venue{enrichResult.venue.cached ? ' (cached)' : ''}
                                                {acceptedKeys.has('venue') && <AcceptedMark> ✓ accepted</AcceptedMark>}
                                            </EnrichEntityTitle>
                                            <small>
                                                Proposed: {enrichResult.venue.result?.resolved_name || '—'}
                                                {enrichResult.venue.result?.resolved_address
                                                    ? ` — ${enrichResult.venue.result.resolved_address}`
                                                    : ''}
                                            </small>
                                            <pre>{JSON.stringify(enrichResult.venue.result, null, 2)}</pre>
                                            <EnrichEntityActions>
                                                <SmallButton
                                                    onClick={() => handleAcceptEnrichment({ kind: 'venue' })}
                                                >
                                                    {acceptedKeys.has('venue') ? 'Re-apply' : 'Accept'}
                                                </SmallButton>
                                            </EnrichEntityActions>
                                        </EnrichEntityCard>
                                    )}

                                    {(enrichResult.items || []).map((entry) => {
                                        const proposedKey = `item:${entry.line_number}`;
                                        const proposedAccepted = acceptedKeys.has(proposedKey);
                                        return (
                                            <EnrichEntityCard key={proposedKey}>
                                                <EnrichEntityTitle>
                                                    Item {entry.line_number || '?'}{entry.cached ? ' (cached)' : ''}
                                                    {proposedAccepted && <AcceptedMark> ✓ accepted</AcceptedMark>}
                                                </EnrichEntityTitle>
                                                <small>Proposed: {entry.result?.resolved_product_name || '—'}</small>
                                                <pre>{JSON.stringify(entry.result, null, 2)}</pre>
                                                <EnrichEntityActions>
                                                    {(entry.result?.candidates || []).slice(0, 3).map((c, idx) => {
                                                        const ckey = `item:${entry.line_number}:c${idx}`;
                                                        const cAccepted = acceptedKeys.has(ckey);
                                                        return (
                                                            <SmallButton
                                                                key={idx}
                                                                onClick={() => handleAcceptEnrichment({
                                                                    kind: 'item',
                                                                    lineNumber: entry.line_number,
                                                                    candidateIndex: idx,
                                                                })}
                                                            >
                                                                {cAccepted ? `✓ #${idx + 1}` : `Accept #${idx + 1}`}
                                                            </SmallButton>
                                                        );
                                                    })}
                                                    <SmallButton
                                                        onClick={() => handleAcceptEnrichment({
                                                            kind: 'item',
                                                            lineNumber: entry.line_number,
                                                        })}
                                                    >
                                                        {proposedAccepted ? 'Re-apply proposed' : 'Accept proposed'}
                                                    </SmallButton>
                                                </EnrichEntityActions>
                                            </EnrichEntityCard>
                                        );
                                    })}
                                </EnrichResultsPanel>
                            )}
                        </EnrichSection>
                        )}

                        {!isProcessedView && (
                        <Section>
                            <SectionHeader>
                                <SectionTitle>Gemini Review Preparation</SectionTitle>
                            </SectionHeader>
                            <SectionBody>
                                <MutedText>
                                    Temporary synchronous test path. This runs extraction, matching, enrichment, review, and prepared-state persistence in one request.
                                </MutedText>
                                <ActionRow>
                                    <SecondaryButton
                                        onClick={handleGeminiPrepareReview}
                                        disabled={geminiPrepareLoading || aiLoading || enrichLoading || processing}
                                    >
                                        {geminiPrepareLoading ? 'Running Gemini pipeline…' : 'Run Gemini Prepare Review'}
                                    </SecondaryButton>
                                </ActionRow>
                                {geminiPrepareError && <ErrorBanner>{geminiPrepareError}</ErrorBanner>}
                            </SectionBody>
                        </Section>
                        )}

                        {!isProcessedView && !isRejectedView && (
                        <ProcessActions>
                            <div style={{ width: '100%', marginBottom: 12 }}>
                                {!showRejectForm ? (
                                    <SecondaryButton onClick={() => {
                                        setRejectLanguage((i18n.language || 'hr').startsWith('en') ? 'en' : 'hr');
                                        const lang = (i18n.language || 'hr').startsWith('en') ? 'en' : 'hr';
                                        setRejectMessage(rejectionTemplates[lang].unclear_image);
                                        setShowRejectForm(true);
                                    }} disabled={processing}>
                                        Reject bill
                                    </SecondaryButton>
                                ) : (
                                    <div style={{ display: 'grid', gap: 10, padding: 14, border: `1px solid ${colors.error.border}`, borderRadius: radius.md, background: colors.error.bg }}>
                                        <strong>Message to the user</strong>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <select value={rejectLanguage} onChange={(event) => {
                                                const lang = event.target.value;
                                                setRejectLanguage(lang);
                                                setRejectMessage(rejectionTemplates[lang][rejectReasonCode]);
                                            }}>
                                                <option value="hr">Hrvatski</option>
                                                <option value="en">English</option>
                                            </select>
                                            <select value={rejectReasonCode} onChange={(event) => {
                                                const code = event.target.value;
                                                setRejectReasonCode(code);
                                                setRejectMessage(rejectionTemplates[rejectLanguage][code]);
                                            }}>
                                                <option value="unclear_image">{rejectLanguage === 'hr' ? 'Račun nije jasno uslikan' : 'Image is unclear'}</option>
                                                <option value="not_a_receipt">{rejectLanguage === 'hr' ? 'Dokument nije račun' : 'Document is not a receipt'}</option>
                                                <option value="missing_details">{rejectLanguage === 'hr' ? 'Nedostaju podaci' : 'Missing details'}</option>
                                            </select>
                                        </div>
                                        <textarea value={rejectMessage} onChange={(event) => setRejectMessage(event.target.value)} rows={3} maxLength={1000} required />
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <SecondaryButton onClick={() => setShowRejectForm(false)}>Cancel</SecondaryButton>
                                            <button type="button" onClick={handleRejectBill} disabled={!rejectMessage.trim() || rejecting} style={{ border: 0, borderRadius: 6, padding: '10px 14px', color: '#fff', background: colors.error.text, cursor: 'pointer' }}>
                                                {rejecting ? 'Rejecting…' : 'Reject and notify user'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <PrimaryButton
                                onClick={handleProcess}
                                disabled={processing || !retailerSelection.mode || !venueSelection.mode}
                                $large
                            >
                                {processing ? t('AdminScannedBills.detail.processing.processing') : t('AdminScannedBills.detail.processing.processBill')}
                            </PrimaryButton>
                        </ProcessActions>
                        )}
                    </SectionBody>
                </Section>
            </PageContent>
        </PageContainer>
    );
};

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const BackButton = styled.button`
    padding: 0.5rem 0.875rem;
    border: 1px solid ${colors.border};
    border-radius: ${radius.md};
    background: white;
    color: ${colors.textSecondary};
    cursor: pointer;
    &:hover { background: ${colors.surfaceHover}; }
`;

const StatusBadge = styled.span`
    padding: 0.375rem 0.75rem;
    border-radius: ${radius.full};
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    background: ${({ $status }) => {
        if ($status === 'processed') return colors.success.bg;
        if ($status === 'pending_retailer') return colors.warning.bg;
        if ($status === 'retailer_rejected') return colors.error.bg;
        return colors.info.bg;
    }};
    color: ${({ $status }) => {
        if ($status === 'processed') return colors.success.text;
        if ($status === 'pending_retailer') return colors.warning.text;
        if ($status === 'retailer_rejected') return colors.error.text;
        return colors.info.text;
    }};
`;

const BillInfoCard = styled.div`
    background: linear-gradient(135deg, ${colors.primaryLight} 0%, white 100%);
    border: 1px solid ${colors.border};
    border-radius: ${radius.lg};
    padding: 1.25rem;
    margin-bottom: 1.5rem;
`;

const BillInfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 1rem;
`;

const BillInfoItem = styled.div`
    label { display: block; font-size: 0.75rem; color: ${colors.textMuted}; margin-bottom: 0.25rem; }
    span { font-size: 0.9375rem; font-weight: 600; color: ${colors.text}; }
`;

const Section = styled.div`
    background: white;
    border: 1px solid ${colors.border};
    border-radius: ${radius.lg};
    margin-bottom: 1.25rem;
    overflow: visible;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    background: ${colors.surfaceHover};
    border-bottom: 1px solid ${colors.borderLight};
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: ${colors.text};
`;

const SectionBody = styled.div`
    padding: 1.25rem;
    padding-bottom: 6rem;
`;

const CacheHint = styled.span`
    font-size: 0.75rem;
    color: ${colors.textMuted};
`;

const AIActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: ${colors.textSecondary};
    cursor: pointer;
    input { width: 16px; height: 16px; }
`;

const ErrorBanner = styled.div`
    margin-top: 0.75rem;
    padding: 0.75rem;
    background: ${colors.error.bg};
    border: 1px solid ${colors.error.border};
    border-radius: ${radius.md};
    color: ${colors.error.text};
    font-size: 0.875rem;
`;

const AIResultBanner = styled.div`
    margin-top: 0.75rem;
    padding: 0.75rem;
    background: ${colors.success.bg};
    border: 1px solid ${colors.success.border};
    border-radius: ${radius.md};
    color: ${colors.success.text};
    font-size: 0.875rem;
`;

const EntityTypeBadge = styled.span`
    display: inline-flex;
    padding: ${({ $small }) => $small ? '0.125rem 0.375rem' : '0.25rem 0.625rem'};
    border-radius: ${radius.full};
    font-size: ${({ $small }) => $small ? '0.625rem' : '0.6875rem'};
    font-weight: 700;
    text-transform: uppercase;
    background: ${({ $isVirtual }) => $isVirtual ? colors.virtual.bg : colors.real.bg};
    color: ${({ $isVirtual }) => $isVirtual ? colors.virtual.text : colors.real.text};
    border: 1px solid ${({ $isVirtual }) => $isVirtual ? colors.virtual.border : colors.real.border};
`;

const ExtractedData = styled.div`
    padding: 0.625rem 0.875rem;
    background: ${colors.info.bg};
    border-radius: ${radius.md};
    font-size: 0.875rem;
    color: ${colors.info.text};
    margin-bottom: 1rem;
    label { font-weight: 600; margin-right: 0.5rem; }
`;

const CandidatesBox = styled.div`
    margin-bottom: 1rem;
    padding: 0.875rem;
    background: ${colors.surfaceHover};
    border-radius: ${radius.md};
`;

const CandidatesTitle = styled.div`
    font-size: 0.75rem;
    font-weight: 600;
    color: ${colors.textMuted};
    text-transform: uppercase;
    margin-bottom: 0.625rem;
`;

const CandidateRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.625rem;
    background: white;
    border-radius: ${radius.sm};
    margin-bottom: 0.375rem;
    &:last-child { margin-bottom: 0; }
`;

const CandidateInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    .name { font-weight: 500; }
    .confidence { font-size: 0.75rem; color: ${colors.textMuted}; }
`;

const SelectedBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem;
    background: ${({ $isVirtual }) => $isVirtual ? colors.virtual.bg : colors.real.bg};
    border: 1px solid ${({ $isVirtual }) => $isVirtual ? colors.virtual.border : colors.real.border};
    border-radius: ${radius.md};
    margin-bottom: 1rem;
`;

const SelectedInfo = styled.div`
    font-size: 0.875rem;
    color: ${colors.text};
    .confidence { margin-left: 0.5rem; font-size: 0.75rem; color: ${colors.textMuted}; }
`;

const SelectedInline = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.625rem;
    background: ${colors.surfaceHover};
    border-radius: ${radius.md};
    font-size: 0.875rem;
`;

const ConfidenceBadge = styled.span`
    font-size: 0.6875rem;
    padding: 0.125rem 0.375rem;
    background: ${colors.success.bg};
    color: ${colors.success.text};
    border-radius: ${radius.sm};
`;

const SmallButton = styled.button`
    padding: 0.375rem 0.75rem;
    border: 1px solid ${colors.primary};
    border-radius: ${radius.sm};
    background: white;
    color: ${colors.primary};
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    &:hover { background: ${colors.primaryLight}; }
`;

const OptionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
`;

const OptionCard = styled.div`
    padding: 1rem;
    border: 2px solid ${({ $active }) => $active ? colors.primary : colors.border};
    border-radius: ${radius.md};
    cursor: pointer;
    transition: all 0.15s ease;
    background: ${({ $active }) => $active ? colors.primaryLight : 'white'};
    &:hover { border-color: ${colors.primary}; }
`;

const OptionTitle = styled.div`
    font-weight: 600;
    font-size: 0.875rem;
    color: ${colors.text};
    margin-bottom: 0.25rem;
`;

const OptionDesc = styled.div`
    font-size: 0.8125rem;
    color: ${colors.textSecondary};
    margin-bottom: 0.75rem;
`;

const OptionContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
`;

const OptionLabelRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const TextInput = styled.input`
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: 1px solid ${colors.border};
    border-radius: ${radius.md};
    font-size: 0.875rem;
    &:focus { outline: none; border-color: ${colors.primary}; box-shadow: 0 0 0 2px ${colors.primaryLight}; }
`;

const TextArea = styled.textarea`
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: 1px solid ${colors.border};
    border-radius: ${radius.md};
    font-size: 0.875rem;
    resize: vertical;
    &:focus { outline: none; border-color: ${colors.primary}; box-shadow: 0 0 0 2px ${colors.primaryLight}; }
`;

const ItemCard = styled.div`
    padding: 1rem;
    border: 1px solid ${colors.border};
    border-radius: ${radius.md};
    margin-bottom: 0.75rem;
    &:last-child { margin-bottom: 0; }
`;

const ItemHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
`;

const ItemName = styled.div`
    font-weight: 600;
    font-size: 0.9375rem;
    color: ${colors.text};
`;

const ItemPrice = styled.div`
    font-weight: 600;
    font-size: 0.875rem;
    color: ${colors.primary};
`;

const ItemMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    font-size: 0.75rem;
    color: ${colors.textMuted};
    margin-bottom: 0.75rem;
`;

/* --- garancija, uparivanja, kadar: prikaz onoga sto v3 vec zna --- */

const WarrantyList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    margin-bottom: 0.75rem;
`;

const WarrantyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.4375rem 0.625rem;
    font-size: ${type.meta};
    border-radius: ${radius.sm};
    border: 1px solid ${p => p.$effective ? colors.success.border : colors.borderLight};
    background: ${p => p.$effective ? colors.success.bg : colors.surfaceHover};
    /* Teritorijalno nevazece pravo se prikazuje, ali prigueno: postoji na
       papiru i ne vrijedi ovdje, i oboje mora biti vidljivo. */
    opacity: ${p => p.$voided ? 0.55 : 1};
    text-decoration: ${p => p.$voided ? 'line-through' : 'none'};
`;

const WarrantyMonths = styled.span`
    font-weight: 700;
    font-size: ${type.dense};
    min-width: 58px;
    color: ${p => p.$effective ? colors.success.text : colors.text};
`;

const WarrantyKind = styled.span`
    color: ${colors.textSecondary};
    flex: 1;
`;

const WarrantyNote = styled.div`
    font-size: ${type.label};
    color: ${colors.textMuted};
    padding: 0 0.625rem 0.25rem 4.25rem;
    line-height: 1.45;
`;

const EvidenceTable = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
`;

const EvidenceRow = styled.div`
    display: grid;
    grid-template-columns: 150px 96px 1fr auto;
    gap: 0.625rem;
    align-items: center;
    padding: 0.5rem 0.625rem;
    font-size: ${type.meta};
    background: ${colors.surfaceHover};
    border: 1px solid ${colors.borderLight};
    border-radius: ${radius.sm};

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
        gap: 0.25rem;
    }
`;

const ProofBadge = styled.span`
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: ${radius.full};
    font-size: ${type.label};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    background: ${p => p.$proof ? colors.success.bg : colors.warning.bg};
    color: ${p => p.$proof ? colors.success.text : colors.warning.text};
    border: 1px solid ${p => p.$proof ? colors.success.border : colors.warning.border};
`;

const VerdictBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    border-radius: ${radius.md};
    font-size: ${type.dense};
    background: ${p => p.$ok ? colors.success.bg : colors.warning.bg};
    color: ${p => p.$ok ? colors.success.text : colors.warning.text};
    border: 1px solid ${p => p.$ok ? colors.success.border : colors.warning.border};
`;

const VerdictMark = styled.span`
    font-size: ${type.title};
    line-height: 1.2;
`;

const VerdictBody = styled.div`
    flex: 1;

    strong { display: block; margin-bottom: 0.1875rem; }
    ul { margin: 0.375rem 0 0; padding-left: 1.125rem; }
    li { margin-bottom: 0.125rem; }
`;

const VerdictMeta = styled.div`
    margin-top: 0.375rem;
    font-size: ${type.label};
    opacity: 0.8;
`;

const ProgressBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin-bottom: 0.875rem;
`;

const ProgressStep = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.625rem;
    border-radius: ${radius.full};
    font-size: ${type.label};
    font-weight: 600;
    background: ${p => p.$state === 'done' ? colors.success.bg
        : p.$state === 'skipped' ? colors.borderLight
        : p.$state === 'next' ? colors.info.bg : colors.surfaceHover};
    color: ${p => p.$state === 'done' ? colors.success.text
        : p.$state === 'skipped' ? colors.textMuted
        : p.$state === 'next' ? colors.info.text : colors.textMuted};
    border: 1px solid ${p => p.$state === 'done' ? colors.success.border
        : p.$state === 'next' ? colors.info.border : colors.borderLight};
`;

const StageGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.625rem;
`;

const StageCard = styled.div`
    padding: 0.625rem 0.75rem;
    border-radius: ${radius.sm};
    border: 1px solid ${p => p.$failed ? colors.error.border : colors.borderLight};
    background: ${p => p.$failed ? colors.error.bg : colors.surfaceHover};
`;

const StageName = styled.div`
    font-size: ${type.label};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${colors.textSecondary};
    margin-bottom: 0.25rem;
`;

const StageTokens = styled.div`
    font-size: ${type.lead};
    font-weight: 700;
    color: ${colors.text};
`;

const StageDetail = styled.div`
    font-size: ${type.label};
    color: ${colors.textMuted};
    margin-top: 0.125rem;
`;

const CaptureIssue = styled.span`
    display: inline-block;
    padding: 0.125rem 0.5rem;
    margin: 0 0.25rem 0.25rem 0;
    border-radius: ${radius.full};
    font-size: ${type.label};
    background: ${colors.warning.bg};
    color: ${colors.warning.text};
    border: 1px solid ${colors.warning.border};
`;

const RetakeBanner = styled.div`
    padding: 0.5rem 0.75rem;
    margin-top: 0.5rem;
    border-radius: ${radius.sm};
    font-size: ${type.meta};
    background: ${colors.error.bg};
    color: ${colors.error.text};
    border: 1px solid ${colors.error.border};
`;

const EmptyHint = styled.div`
    font-size: ${type.meta};
    color: ${colors.textMuted};
    padding: 0.375rem 0;
`;

const ItemEditGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.625rem;
    margin-bottom: 0.875rem;
`;

const ItemEditField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    label {
        font-size: 0.6875rem;
        font-weight: 600;
        color: ${colors.textSecondary};
        text-transform: uppercase;
    }
`;

const MatchedInfoBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
    padding: 0.625rem 0.75rem;
    border: 1px solid ${colors.borderLight};
    border-radius: ${radius.sm};
    background: ${colors.surfaceHover};
    font-size: 0.75rem;
    color: ${colors.textSecondary};

    strong {
        color: ${colors.text};
        font-weight: 600;
    }
`;

const ItemFields = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
`;

const ItemField = styled.div``;

const ItemFieldLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${colors.textSecondary};
    margin-bottom: 0.5rem;
    text-transform: uppercase;
`;

const ItemFieldOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const CreateInlineInput = styled.input`
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px dashed ${colors.border};
    border-radius: ${radius.md};
    font-size: 0.8125rem;
    background: ${colors.surfaceHover};
    &:focus { outline: none; border-color: ${colors.primary}; border-style: solid; }
    &::placeholder { color: ${colors.textMuted}; }
`;

const PreviewBox = styled.div`
    padding: 1.25rem;
    background: ${colors.surfaceHover};
    border-radius: ${radius.md};
    margin-bottom: 1.25rem;
`;

const PreviewTitle = styled.div`
    font-weight: 600;
    font-size: 0.875rem;
    color: ${colors.text};
    margin-bottom: 1rem;
`;

const PreviewList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
`;

const PreviewItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.625rem;
    background: white;
    border-radius: ${radius.sm};
`;

const PreviewIcon = styled.div`
    font-size: 1.125rem;
`;

const PreviewText = styled.div`
    flex: 1;
    font-size: 0.875rem;
    color: ${colors.text};
    small { color: ${colors.textSecondary}; }
`;

const PreviewTag = styled.span`
    display: inline-flex;
    margin-left: 0.5rem;
    padding: 0.125rem 0.5rem;
    border-radius: ${radius.sm};
    font-size: 0.6875rem;
    font-weight: 600;
    background: ${({ $type }) => {
        if ($type === 'create') return colors.virtual.bg;
        if ($type === 'real') return colors.real.bg;
        if ($type === 'virtual') return colors.virtual.bg;
        if ($type === 'approval') return colors.warning.bg;
        return colors.info.bg;
    }};
    color: ${({ $type }) => {
        if ($type === 'create') return colors.virtual.text;
        if ($type === 'real') return colors.real.text;
        if ($type === 'virtual') return colors.virtual.text;
        if ($type === 'approval') return colors.warning.text;
        return colors.info.text;
    }};
`;

const PreviewDivider = styled.hr`
    border: none;
    border-top: 1px solid ${colors.border};
    margin: 1.25rem 0;
`;

const PreviewOutcome = styled.div``;

const OutcomeBox = styled.div`
    display: flex;
    gap: 1rem;
    padding: 1rem;
    border-radius: ${radius.md};
    background: ${({ $type }) => $type === 'virtual' ? colors.info.bg : colors.warning.bg};
    border: 1px solid ${({ $type }) => $type === 'virtual' ? colors.info.border : colors.warning.border};
`;

const OutcomeIcon = styled.div`
    font-size: 1.5rem;
`;

const OutcomeText = styled.div`
    strong { display: block; margin-bottom: 0.375rem; color: ${colors.text}; }
    p { margin: 0 0 0.375rem; font-size: 0.875rem; color: ${colors.textSecondary}; &:last-child { margin-bottom: 0; } }
`;

const NotesField = styled.div`
    margin-bottom: 1.25rem;
    label { display: block; font-size: 0.875rem; font-weight: 500; color: ${colors.textSecondary}; margin-bottom: 0.5rem; }
`;

const ProcessActions = styled.div`
    display: flex;
    justify-content: flex-end;
    position: sticky;
    bottom: 0;
    z-index: 2;
    margin: 1.5rem -1.25rem -1.25rem;
    padding: 1rem 1.25rem 1.25rem;
    background: linear-gradient(to top, white 78%, rgba(255, 255, 255, 0));
    border-top: 1px solid ${colors.borderLight};

    @media (max-width: 768px) {
        margin-left: -1rem;
        margin-right: -1rem;
        margin-bottom: -1rem;
        padding-left: 1rem;
        padding-right: 1rem;
        padding-bottom: 1rem;
    }
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: ${({ $large }) => $large ? '0.875rem 1.75rem' : '0.625rem 1.125rem'};
    border: none;
    border-radius: ${radius.md};
    background: ${colors.primary};
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    &:hover:not(:disabled) { background: ${colors.primaryHover}; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border: 1px solid ${colors.border};
    border-radius: ${radius.md};
    background: ${colors.surface};
    color: ${colors.text};
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    &:hover:not(:disabled) { background: ${colors.surfaceHover}; border-color: ${colors.primary}; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const OcrToolbar = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
`;

const OcrField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: min(100%, 320px);

    label {
        font-size: 0.75rem;
        font-weight: 700;
        color: ${colors.textSecondary};
    }

    select {
        min-height: 40px;
        border: 1px solid ${colors.border};
        border-radius: ${radius.md};
        padding: 0.5rem 0.625rem;
        color: ${colors.text};
        background: ${colors.surface};
    }
`;

const VerifyModalGrid = styled.div`
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    gap: 1rem;

    @media (max-width: 980px) {
        grid-template-columns: 1fr;
    }
`;

const VerifyColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
`;

const VerifyBlock = styled.div`
    border: 1px solid ${colors.border};
    border-radius: ${radius.md};
    background: ${colors.surface};
    padding: 0.75rem;
`;

const VerifyBlockTitle = styled.div`
    font-size: 0.8125rem;
    font-weight: 700;
    color: ${colors.text};
    margin-bottom: 0.625rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const ImageList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
`;

const ImageCard = styled.div`
    border: 1px solid ${colors.borderLight};
    border-radius: ${radius.sm};
    overflow: hidden;
    background: ${colors.surfaceHover};
`;

const BillImagePreview = styled.img`
    display: block;
    width: 100%;
    max-height: 420px;
    object-fit: contain;
    background: #fff;
`;

const ImageLink = styled.a`
    display: inline-block;
    padding: 0.5rem 0.625rem;
    color: ${colors.primary};
    font-size: 0.75rem;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`;

const CodeBlock = styled.pre`
    margin: 0;
    max-height: 320px;
    overflow: auto;
    padding: 0.625rem;
    border-radius: ${radius.sm};
    border: 1px solid ${colors.borderLight};
    background: ${colors.surfaceHover};
    color: ${colors.text};
    font-size: 0.75rem;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
`;

const MutedText = styled.div`
    color: ${colors.textMuted};
    font-size: 0.8125rem;
`;

const ActionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

const VerifyItemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 320px;
    overflow: auto;
`;

const VerifyItemRow = styled.div`
    border: 1px solid ${colors.borderLight};
    border-radius: ${radius.sm};
    padding: 0.625rem;
    background: ${colors.surfaceHover};
    color: ${colors.text};
    font-size: 0.75rem;

    small {
        color: ${colors.textSecondary};
    }
`;

const CurrencySourceBadge = styled.span`
    display: inline-block;
    margin-left: 0.375rem;
    padding: 1px 0.375rem;
    border-radius: ${radius.sm};
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    background: ${({ $source }) => {
        switch ($source) {
            case 'observed': return colors.success.bg;
            case 'inferred': return colors.warning.bg;
            case 'manual': return colors.info.bg;
            case 'reviewed': return colors.primaryLight;
            case 'existing': return colors.primaryLight;
            default: return colors.borderLight;
        }
    }};
    color: ${({ $source }) => {
        switch ($source) {
            case 'observed': return colors.success.text;
            case 'inferred': return colors.warning.text;
            case 'manual': return colors.info.text;
            case 'reviewed': return colors.primary;
            case 'existing': return colors.primary;
            default: return colors.textMuted;
        }
    }};
`;

const NeedsEnrichmentLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.6875rem;
    color: ${colors.textSecondary};
    cursor: pointer;
    input { width: 14px; height: 14px; }
`;

const EnrichSection = styled.div`
    margin: 1.25rem 0;
    padding: 1rem;
    border: 1px dashed ${colors.border};
    border-radius: ${radius.md};
    background: ${colors.surface};
`;

const EnrichHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.625rem;
    strong { font-size: 0.875rem; color: ${colors.text}; }
    span { font-size: 0.75rem; color: ${colors.textSecondary}; }
`;

const EnrichActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
`;

const EnrichResultsPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem;
    background: ${colors.surfaceHover};
    border-radius: ${radius.md};
`;

const EnrichResultsHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
    strong { font-size: 0.875rem; color: ${colors.text}; }
    small { font-size: 0.6875rem; color: ${colors.textMuted}; }
`;

const EnrichEntityCard = styled.div`
    padding: 0.75rem;
    background: white;
    border: 1px solid ${colors.borderLight};
    border-radius: ${radius.md};
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    pre {
        margin: 0;
        max-height: 220px;
        overflow: auto;
        padding: 0.5rem;
        background: ${colors.surfaceHover};
        border-radius: ${radius.sm};
        font-size: 0.6875rem;
        color: ${colors.text};
    }

    small {
        font-size: 0.75rem;
        color: ${colors.textSecondary};
    }
`;

const EnrichEntityTitle = styled.div`
    font-size: 0.8125rem;
    font-weight: 700;
    color: ${colors.text};
`;

const EnrichEntityActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
`;

const AcceptedMark = styled.span`
    display: inline-block;
    margin-left: 0.5rem;
    padding: 0.125rem 0.375rem;
    border-radius: ${radius.sm};
    font-size: 0.6875rem;
    font-weight: 600;
    background: ${colors.success.bg};
    color: ${colors.success.text};
    border: 1px solid ${colors.success.border};
`;

export default AdminScannedBillDetail;
